import { 
  collection, 
  getDocs, 
  doc, 
  getDoc, 
  updateDoc, 
  addDoc,
  deleteDoc,
  setDoc,
  query, 
  where, 
  orderBy, 
  limit,
  startAfter,
  DocumentSnapshot,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { AppUser, EarningApplication, UserReport } from '../types';

// db'yi re-export et
export { db };

// Satış kayıtları için tip tanımı
export interface SaleRecord {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  productType: string; // 'premium_subscription', 'user_subscription', 'boost', etc.
  productId: string;
  amount: number;
  currency: string;
  paymentMethod: string;
  status: string; // 'completed', 'pending', 'failed', 'refunded'
  createdAt: any;
  metadata?: any;
}

// Kullanıcı servisleri
export const userService = {
  // Tüm kullanıcıları getir (hızlı versiyon - premium/subscriber bilgileri olmadan)
  async getAllUsers(limitCount?: number, lastDoc?: DocumentSnapshot) {
    try {
      // Tüm kullanıcıları getir - orderBy kaldırıldı ki eksik createdAt olan kullanıcılar da gelsin
      let userQuery = query(collection(db, 'users'));

      // Eğer limit belirtildiyse ekle
      if (limitCount) {
        userQuery = query(userQuery, limit(limitCount));
      }

      if (lastDoc) {
        userQuery = query(userQuery, startAfter(lastDoc));
      }

      const snapshot = await getDocs(userQuery);
      const users: AppUser[] = [];
      let lastDocument = null;

      snapshot.forEach((doc) => {
        const userData = doc.data();
        
        users.push({
          id: doc.id,
          email: userData.email || '',
          name: userData.name || '',
          age: userData.age || 18,
          gender: userData.gender || '', // Gender alanını ekle
          isActive: userData.isActive !== false, // default true
          createdAt: userData.createdAt,
          lastActiveAt: userData.lastActiveAt,
          photos: userData.photos || [],
          bio: userData.bio || '',
          interests: userData.interests || [],
          relationshipStatus: userData.relationshipStatus || '',
          languages: userData.languages || [],
          // Google ve sosyal medya profil resimleri için ek alanlar
          photoURL: userData.photoURL || null,
          profilePictureUrl: userData.profilePictureUrl || null,
          profilePicUrl: userData.profilePicUrl || null,
          googlePhotoURL: userData.googlePhotoURL || null
          // Premium/subscriber bilgileri kaldırıldı - performans için
        });
        lastDocument = doc;
      });

      // Kullanıcıları client-side'da createdAt'e göre sırala (en yeni önce)
      users.sort((a, b) => {
        const dateA = a.createdAt;
        const dateB = b.createdAt;
        
        // Eğer tarih yoksa en aşağıya koy
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        
        // Timestamp objeleri için karşılaştırma
        let timeA, timeB;
        
        try {
          if (dateA.toDate) {
            timeA = dateA.toDate().getTime();
          } else {
            timeA = new Date(dateA).getTime();
          }
        } catch (error) {
          timeA = 0;
        }
        
        try {
          if (dateB.toDate) {
            timeB = dateB.toDate().getTime();
          } else {
            timeB = new Date(dateB).getTime();
          }
        } catch (error) {
          timeB = 0;
        }
        
        // Büyükten küçüğe sırala (en yeni en üstte)
        return timeB - timeA;
      });

      console.log(`📊 getAllUsers: ${users.length} kullanıcı yüklendi ${limitCount ? `(limit: ${limitCount})` : '(limit yok - tümü)'} (client-side sıralama)`);

      return { users, lastDocument, hasMore: limitCount ? snapshot.docs.length === limitCount : false };
    } catch (error) {
      console.error('Kullanıcıları getirme hatası:', error);
      throw error;
    }
  },

  // Kullanıcının premium abonelik bilgilerini getir
  async getUserPremiumInfo(userId: string) {
    try {
      // Premium abonelik collection'ını kontrol et
      const premiumQuery = query(
        collection(db, 'premium_subscriptions'),
        where('userId', '==', userId),
        where('isActive', '==', true)
      );
      
      const premiumSnapshot = await getDocs(premiumQuery);
      
      if (!premiumSnapshot.empty) {
        const premiumDoc = premiumSnapshot.docs[0];
        const premiumData = premiumDoc.data();
        
        return {
          isPremium: true,
          packageType: premiumData.packageType || 'unknown', // '1_month', '3_month', '6_month', '12_month'
          price: premiumData.price || 0,
          startDate: premiumData.startDate,
          endDate: premiumData.endDate,
          isActive: premiumData.isActive || false
        };
      }
      
      return {
        isPremium: false,
        packageType: null,
        price: 0,
        startDate: null,
        endDate: null,
        isActive: false
      };
    } catch (error) {
      console.error('Premium bilgi getirme hatası:', error);
      return {
        isPremium: false,
        packageType: null,
        price: 0,
        startDate: null,
        endDate: null,
        isActive: false
      };
    }
  },

  // Kullanıcının abone sayısını getir (sadece subscriptions collection'ından)
  async getUserSubscriberCount(userId: string) {
    try {
      console.log('🔍 Firebase service abone sayısı hesaplanıyor, userId:', userId);
      
      // Sadece subscriptions collection'ından aktif aboneleri say
      const subscriberQuery = query(
        collection(db, 'subscriptions'),
        where('subscribedToId', '==', userId),
        where('status', '==', 'active')
      );
      
      const subscriberSnapshot = await getDocs(subscriberQuery);
      const totalCount = subscriberSnapshot.size;
      
      console.log('📊 subscriptions collection\'dan toplam abone sayısı:', totalCount);
      
      return totalCount;
    } catch (error) {
      console.error('💥 Abone sayısı getirme hatası:', error);
      return 0;
    }
  },

  // Aktif kullanıcı sayısı
  async getActiveUsersCount() {
    try {
      const activeQuery = query(
        collection(db, 'users'),
        where('isActive', '==', true)
      );
      const snapshot = await getDocs(activeQuery);
      return snapshot.size;
    } catch (error) {
      console.error('Aktif kullanıcı sayısı hatası:', error);
      return 0;
    }
  },

  // Toplam kullanıcı sayısı
  async getTotalUsersCount() {
    try {
      const snapshot = await getDocs(collection(db, 'users'));
      return snapshot.size;
    } catch (error) {
      console.error('Toplam kullanıcı sayısı hatası:', error);
      return 0;
    }
  },

  // Bugün kayıt olan kullanıcılar
  async getTodayNewUsers() {
    try {
      console.log('🔍 Bugünkü yeni kullanıcılar hesaplanıyor...');
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayStart = Timestamp.fromDate(today);
      
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowStart = Timestamp.fromDate(tomorrow);

      console.log('📅 Bugün başlangıç:', todayStart.toDate());
      console.log('📅 Yarın başlangıç:', tomorrowStart.toDate());

      // Artık gerçek createdAt alanını kullanabiliriz
      try {
        const todayQuery = query(
          collection(db, 'users'),
          where('createdAt', '>=', todayStart),
          where('createdAt', '<', tomorrowStart)
        );
        
        const todaySnapshot = await getDocs(todayQuery);
        const todayCount = todaySnapshot.size;
        
        console.log('✅ Bugün kayıt olan kullanıcı sayısı (createdAt ile):', todayCount);
        
        // Bugün kayıt olanların detaylarını göster
        if (todayCount > 0) {
          console.log('👥 Bugün kayıt olanlar:');
          todaySnapshot.docs.forEach(doc => {
            const data = doc.data();
            const createdAt = data.createdAt?.toDate?.();
            console.log(`  - ${data.name}: ${createdAt?.toISOString()}`);
          });
        }
        
        return todayCount;
      } catch (queryError) {
        console.log('⚠️ createdAt sorgusu başarısız, fallback yöntemi deneniyor...', queryError);
        
        // Fallback: Tüm kullanıcıları çekip manuel kontrol et
        const allUsersSnapshot = await getDocs(collection(db, 'users'));
        console.log('👥 Toplam kullanıcı sayısı:', allUsersSnapshot.size);
        
        let todayCount = 0;
        let createdAtCount = 0;
        let lastActiveCount = 0;
        
        allUsersSnapshot.docs.forEach(doc => {
          const userData = doc.data();
          const createdAt = userData.createdAt;
          const lastActive = userData.lastActive;
          
          // Önce createdAt'i dene
          if (createdAt) {
            createdAtCount++;
            try {
              let userDate;
              if (createdAt.toDate) {
                userDate = createdAt.toDate();
              } else if (createdAt.seconds) {
                userDate = new Date(createdAt.seconds * 1000);
              } else {
                userDate = new Date(createdAt);
              }
              
              if (userDate >= today && userDate < tomorrow) {
                todayCount++;
              }
            } catch (dateError) {
              console.error('createdAt parse hatası:', dateError);
            }
          } else if (lastActive) {
            // createdAt yoksa lastActive kullan (yaklaşık hesaplama)
            lastActiveCount++;
            try {
              let userDate;
              if (lastActive.toDate) {
                userDate = lastActive.toDate();
              } else if (lastActive.seconds) {
                userDate = new Date(lastActive.seconds * 1000);
              } else {
                userDate = new Date(lastActive);
              }
              
              if (userDate >= today && userDate < tomorrow) {
                todayCount++;
              }
            } catch (dateError) {
              console.error('lastActive parse hatası:', dateError);
            }
          }
        });
        
        console.log(`📊 Kullanıcı alan istatistikleri:`);
        console.log(`  - createdAt alanı olan: ${createdAtCount}`);
        console.log(`  - lastActive alanı olan: ${lastActiveCount}`);
        console.log(`  - Bugün kayıt olan/aktif olan: ${todayCount}`);
        
        return todayCount;
      }
    } catch (error) {
      console.error('Bugünkü yeni kullanıcı sayısı hatası:', error);
      return 0;
    }
  },

  // Kullanıcı durumunu güncelle
  async updateUserStatus(userId: string, isActive: boolean) {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, { isActive });
      return true;
    } catch (error) {
      console.error('Kullanıcı durumu güncelleme hatası:', error);
      throw error;
    }
  },

  // Admin tarafından kullanıcıya manuel abone ekleme
  async addManualSubscriber(targetUserId: string, adminNote?: string) {
    try {
      console.log('🔧 Admin tarafından manuel abone ekleniyor:', targetUserId);
      
      // Gerçek admin ID'sini kullan (datezy@admin.com kullanıcısının ID'si)
      const adminUserId = 'Z02SfMSJRLf6a06nhJDqd5AoOPk1';
      
      // Gerçek abonelik kaydı oluştur - subscriptions collection'ına ekle
      const subscriptionData = {
        subscriberId: adminUserId, // Gerçek admin ID'si
        subscribedToId: targetUserId,
        planType: '1 Aylık', // Standart 1 aylık plan
        price: 49.99, // Standart fiyat
        startDate: new Date(),
        endDate: new Date(Date.now() + (30 * 24 * 60 * 60 * 1000)), // 30 gün sonra
        status: 'active',
        createdAt: new Date(),
        adminNote: adminNote || 'Admin tarafından manuel eklendi',
        metadata: {
          addedByAdmin: true,
          adminTimestamp: new Date().toISOString(),
          subscriberType: 'admin_manual'
        }
      };

      await addDoc(collection(db, 'subscriptions'), subscriptionData);
      
      console.log('✅ Manuel abone başarıyla eklendi:', adminUserId, '->', targetUserId);
      return true;
    } catch (error) {
      console.error('❌ Manuel abone ekleme hatası:', error);
      throw error;
    }
  },

  // Kullanıcının detaylı abone listesini getir (sadece subscriptions collection'ından)
  async getUserSubscribersDetailed(userId: string) {
    try {
      console.log('📊 Kullanıcının abone detayları alınıyor:', userId);
      
      // subscriptions collection'ından bu kullanıcıya abone olanları al
      const subscribersQuery = query(
        collection(db, 'subscriptions'),
        where('subscribedToId', '==', userId),
        where('status', '==', 'active'),
        orderBy('createdAt', 'desc')
      );
      
      const subscribersSnapshot = await getDocs(subscribersQuery);
      const subscribers: any[] = [];
      
      for (const docSnap of subscribersSnapshot.docs) {
        const subData = docSnap.data();
        
        let subscriberDetails: { name: string; email: string; } | undefined = undefined;
        
        // Admin ID'si mi kontrol et
        const adminUserId = 'Z02SfMSJRLf6a06nhJDqd5AoOPk1';
        if (subData.subscriberId === adminUserId) {
          // Admin tarafından eklenen abonelik
          subscriberDetails = {
            name: 'Admin Ekledi',
            email: 'admin@datezy.com'
          };
        } else {
          // Normal abonelik - gerçek kullanıcı bilgilerini al
          try {
            const userDoc = await getDoc(doc(db, 'users', subData.subscriberId));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              subscriberDetails = {
                name: userData.name || 'İsimsiz Kullanıcı',
                email: userData.email || 'E-posta yok'
              };
            }
          } catch (error) {
            console.log('Kullanıcı bilgisi alınamadı:', subData.subscriberId);
          }
        }
        
        subscribers.push({
          id: docSnap.id,
          subscriberId: subData.subscriberId,
          planType: subData.planType || 'Bilinmiyor',
          price: subData.price || 0,
          createdAt: subData.createdAt,
          startDate: subData.startDate,
          endDate: subData.endDate,
          adminNote: subData.adminNote,
          addedByAdmin: subData.subscriberId === adminUserId, // Admin ID'si ise true
          subscriberDetails
        });
      }
      
      console.log(`✅ ${subscribers.length} abone detayı alındı`);
      return subscribers;
    } catch (error) {
      console.error('❌ Abone detayları alma hatası:', error);
      throw error;
    }
  },

  // Manuel abone silme - subscriptions collection'ından sil
  async removeManualSubscriber(subscriptionId: string) {
    try {
      // subscriptions collection'ından belirtilen ID'yi sil
      await updateDoc(doc(db, 'subscriptions', subscriptionId), {
        status: 'cancelled',
        cancelledAt: new Date(),
        cancelledByAdmin: true
      });
      
      console.log('✅ Manuel abone başarıyla silindi:', subscriptionId);
      return true;
    } catch (error) {
      console.error('❌ Manuel abone silme hatası:', error);
      throw error;
    }
  },

  // Kullanıcıya manuel jeton ekleme
  async addTokensToUser(userId: string, tokenAmount: number, reason?: string) {
    try {
      console.log('🪙 Kullanıcıya manuel jeton ekleniyor:', userId, 'miktar:', tokenAmount);
      
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        throw new Error('Kullanıcı bulunamadı');
      }
      
      const userData = userDoc.data();
      const currentTokens = userData.totalTokens || 0;
      const newTokens = currentTokens + tokenAmount;
      
      await updateDoc(userRef, {
        totalTokens: newTokens,
        tokenHistory: userData.tokenHistory || [] // Gelecekte token geçmişi tutmak için
      });
      
      console.log(`✅ Kullanıcının jetonu güncellendi: ${currentTokens} -> ${newTokens}`);
      console.log(`📝 Sebep: ${reason || 'Admin tarafından manuel eklendi'}`);
      
      return {
        previousTokens: currentTokens,
        newTokens: newTokens,
        addedTokens: tokenAmount
      };
    } catch (error) {
      console.error('❌ Manual jeton ekleme hatası:', error);
      throw error;
    }
  },

  // Toplu manuel abone ekleme  
  async addBulkManualSubscribers(targetUserId: string, count: number, adminNote?: string) {
    try {
      console.log('🔧 Toplu manuel abone ekleniyor:', targetUserId, 'adet:', count);
      
      const adminUserId = 'Z02SfMSJRLf6a06nhJDqd5AoOPk1';
      const promises = [];
      const now = new Date();
      
      for (let i = 0; i < count; i++) {
        const subscriptionData = {
          subscriberId: adminUserId,
          subscribedToId: targetUserId,
          planType: '1 Aylık',
          price: 49.99,
          startDate: now,
          endDate: new Date(now.getTime() + (30 * 24 * 60 * 60 * 1000)),
          status: 'active',
          createdAt: now,
          adminNote: adminNote || `Toplu ekleme ${i + 1}/${count}`,
          metadata: {
            addedByAdmin: true,
            adminTimestamp: now.toISOString(),
            subscriberType: 'admin_bulk',
            bulkIndex: i + 1,
            bulkTotal: count
          }
        };

        promises.push(addDoc(collection(db, 'subscriptions'), subscriptionData));
      }
      
      await Promise.all(promises);
      console.log(`✅ ${count} adet manuel abone başarıyla eklendi`);
      return true;
    } catch (error) {
      console.error('❌ Toplu manuel abone ekleme hatası:', error);
      throw error;
    }
  },

  // Kullanıcı silme (soft delete - deleted_users collection'ına taşı)
  async deleteUser(userId: string, adminReason?: string) {
    try {
      console.log('🗑️ Firebase Service: Kullanıcı siliniyor, userId:', userId);
      
      // Önce kullanıcı bilgilerini al
      console.log('1️⃣ Kullanıcı bilgileri alınıyor...');
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        console.error('❌ Kullanıcı bulunamadı:', userId);
        throw new Error('Kullanıcı bulunamadı');
      }
      
      const userData = userDoc.data();
      console.log('✅ Kullanıcı bilgileri alındı:', userData.name, userData.email);
      
      console.log('2️⃣ Abone sayısı hesaplanıyor...');
      const subscriberCount = await this.getUserSubscriberCount(userId);
      console.log('✅ Abone sayısı:', subscriberCount);
      
      console.log('3️⃣ Silinen kullanıcı verisi hazırlanıyor...');
      
      // userData'daki undefined değerleri temizle
      const cleanUserData: any = {};
      Object.keys(userData).forEach(key => {
        if (userData[key] !== undefined && userData[key] !== null) {
          cleanUserData[key] = userData[key];
        }
      });
      
      // Undefined değerleri temizleyerek güvenli veri oluştur
      const deletedUserData: any = {
        originalUserId: userId,
        userData: cleanUserData,
        deletedAt: new Date(),
        deletedBy: 'admin',
        deletionReason: adminReason || 'Admin tarafından silindi',
        email: userData.email || 'Bilinmiyor',
        name: userData.name || 'İsimsiz Kullanıcı',
        totalSubscribers: subscriberCount || 0
      };
      
      // originalCreatedAt sadece değeri varsa ekle
      if (userData.createdAt) {
        deletedUserData.originalCreatedAt = userData.createdAt;
      } else {
        deletedUserData.originalCreatedAt = new Date(); // Fallback olarak şimdiki zamanı kullan
      }
      
      console.log('📋 Hazırlanan veri:', deletedUserData);
      
      console.log('4️⃣ deleted_users collection\'ına kaydediliyor...');
      await addDoc(collection(db, 'deleted_users'), deletedUserData);
      console.log('✅ deleted_users collection\'ına kaydedildi');
      
      // Ana users collection'ından kullanıcıyı sil (tamamen sil)
      console.log('5️⃣ users collection\'ından siliniyor...');
      await deleteDoc(userRef);
      console.log('✅ users collection\'ından silindi');
      
      // Kullanıcıya ait tüm aktif abonelikleri iptal et
      console.log('6️⃣ Aktif abonelikler iptal ediliyor...');
      const subscriptionsQuery = query(
        collection(db, 'subscriptions'),
        where('subscribedToId', '==', userId),
        where('status', '==', 'active')
      );
      
      const subscriptionsSnapshot = await getDocs(subscriptionsQuery);
      console.log('📊 İptal edilecek abonelik sayısı:', subscriptionsSnapshot.size);
      
      const updatePromises: Promise<void>[] = [];
      
      subscriptionsSnapshot.docs.forEach((doc) => {
        updatePromises.push(
          updateDoc(doc.ref, {
            status: 'cancelled',
            cancelledAt: new Date(),
            cancelledReason: 'User deleted by admin'
          })
        );
      });
      
      await Promise.all(updatePromises);
      console.log('✅ Tüm abonelikler iptal edildi');
      
      console.log('🎉 Kullanıcı başarıyla silindi ve deleted_users collection\'ına taşındı');
      return true;
    } catch (error) {
      console.error('💥 Kullanıcı silme hatası - detay:', error);
      
      // Hata türüne göre özel mesajlar
      if (error instanceof Error) {
        if (error.message.includes('Missing or insufficient permissions')) {
          throw new Error('Firebase yetki hatası: Silme işlemi için yeterli yetki yok');
        } else if (error.message.includes('offline')) {
          throw new Error('İnternet bağlantısı sorunu: Lütfen bağlantınızı kontrol edin');
        } else {
          throw new Error(`Silme işlemi başarısız: ${error.message}`);
        }
      }
      
      throw error;
    }
  },

  // Silinen kullanıcıları getir
  async getDeletedUsers(limitCount: number = 50, lastDoc?: DocumentSnapshot) {
    try {
      console.log('🗑️ Silinen kullanıcılar getiriliyor...');
      
      let deletedQuery = query(
        collection(db, 'deleted_users'),
        orderBy('deletedAt', 'desc'),
        limit(limitCount)
      );

      if (lastDoc) {
        deletedQuery = query(deletedQuery, startAfter(lastDoc));
      }

      const snapshot = await getDocs(deletedQuery);
      const deletedUsers: any[] = [];
      let lastDocument = null;

      snapshot.forEach((doc) => {
        const data = doc.data();
        deletedUsers.push({
          id: doc.id,
          originalUserId: data.originalUserId,
          name: data.name || data.userData?.name || 'İsimsiz Kullanıcı',
          email: data.email || data.userData?.email || 'E-posta yok',
          deletedAt: data.deletedAt,
          deletedBy: data.deletedBy,
          deletionReason: data.deletionReason,
          originalCreatedAt: data.originalCreatedAt,
          totalSubscribers: data.totalSubscribers || 0,
          userData: data.userData
        });
        lastDocument = doc;
      });

      console.log(`✅ ${deletedUsers.length} silinen kullanıcı getirildi`);
      return { deletedUsers, lastDocument, hasMore: snapshot.docs.length === limitCount };
    } catch (error) {
      console.error('❌ Silinen kullanıcıları getirme hatası:', error);
      throw error;
    }
  },

  // Kullanıcıyı geri yükleme
  async restoreUser(deletedUserId: string, originalUserId: string) {
    try {
      console.log('♻️ Kullanıcı geri yükleniyor:', originalUserId);
      
      // Silinen kullanıcı bilgilerini al
      const deletedUserRef = doc(db, 'deleted_users', deletedUserId);
      const deletedUserDoc = await getDoc(deletedUserRef);
      
      if (!deletedUserDoc.exists()) {
        throw new Error('Silinen kullanıcı kaydı bulunamadı');
      }
      
      const deletedData = deletedUserDoc.data();
      const userData = deletedData.userData;
      
      // Kullanıcıyı users collection'ına yeniden oluştur (set ile)
      const userRef = doc(db, 'users', originalUserId);
      await setDoc(userRef, {
        ...userData,
        restoredAt: new Date(),
        restoredBy: 'admin',
        isDeleted: false
      });
      
      // deleted_users collection'ından kaldır
      await updateDoc(deletedUserRef, {
        restoredAt: new Date(),
        restoredBy: 'admin',
        status: 'restored'
      });
      
      console.log('✅ Kullanıcı başarıyla geri yüklendi');
      return true;
    } catch (error) {
      console.error('❌ Kullanıcı geri yükleme hatası:', error);
      
      // Eğer kullanıcı mevcut değilse yeniden oluştur
      try {
        const deletedUserRef = doc(db, 'deleted_users', deletedUserId);
        const deletedUserDoc = await getDoc(deletedUserRef);
        
        if (deletedUserDoc.exists()) {
          const deletedData = deletedUserDoc.data();
          const userData = deletedData.userData;
          
          // Kullanıcıyı users collection'ına yeniden oluştur (set ile)
          const userRef = doc(db, 'users', originalUserId);
          await setDoc(userRef, {
            ...userData,
            restoredAt: new Date(),
            restoredBy: 'admin',
            isDeleted: false
          });
          
          // deleted_users collection'ından kaldır
          await updateDoc(deletedUserRef, {
            restoredAt: new Date(),
            restoredBy: 'admin',
            status: 'restored'
          });
          
          console.log('✅ Kullanıcı yeniden oluşturularak geri yüklendi');
          return true;
        }
      } catch (retryError) {
        console.error('❌ Kullanıcı yeniden oluşturma hatası:', retryError);
        throw error;
      }
      
      throw error;
    }
  },

  // Silinen kullanıcı sayısını getir
  async getDeletedUsersCount() {
    try {
      const deletedQuery = query(
        collection(db, 'deleted_users'),
        where('status', '!=', 'restored') // Geri yüklenmeyen silinmiş kullanıcıları say
      );
      const snapshot = await getDocs(deletedQuery);
      return snapshot.size;
    } catch (error) {
      console.error('Silinen kullanıcı sayısı hatası:', error);
      return 0;
    }
  },

  // Belirli bir kullanıcıyı ID ile getir
  async getUserById(userId: string) {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        return {
          id: userDoc.id,
          name: userData.name || 'İsimsiz Kullanıcı',
          email: userData.email || 'E-posta yok',
          age: userData.age || 0,
          isActive: userData.isActive !== false,
          createdAt: userData.createdAt,
          lastActiveAt: userData.lastActiveAt,
          photos: userData.photos || [],
          bio: userData.bio || '',
          interests: userData.interests || [],
          relationshipStatus: userData.relationshipStatus || '',
          languages: userData.languages || [],
          subscriberCount: userData.subscriberCount || 0,
        };
      }
      return null;
    } catch (error) {
      console.error('Kullanıcı getirme hatası:', error);
      throw error;
    }
  },

  async create100FakeTurkishWomen(
    progressCallback?: (current: number, total: number, message: string) => void
  ): Promise<{ success: boolean; createdCount: number; errorCount: number; message: string }> {
    const total = 100;
    let createdCount = 0;
    let errorCount = 0;

    // 🔄 HYBRID: Supabase service'ini import et
    let supabaseService: any = null;
    let useSupabase = true;
    
    try {
      const imported = await import('./supabaseService');
      supabaseService = imported.supabaseService;
      console.log('✅ HYBRID: Supabase service başarıyla yüklendi');
    } catch (importError) {
      console.log('⚠️ HYBRID: Supabase service yüklenemedi, sadece Firebase kullanılacak:', importError);
      useSupabase = false;
    }

    // Türk kadın isimleri listesi
    const turkishFemaleNames = [
      'Ayşe', 'Fatma', 'Zeynep', 'Hatice', 'Merve', 'Elif', 'Esra', 'Dilek', 'Sevim', 'Gülhan',
      'Meryem', 'Gamze', 'Özlem', 'Döne', 'Sibel', 'Gonca', 'Nurten', 'Serpil', 'Selma', 'Hacer',
      'Seda', 'Sedef', 'Demet', 'Deniz', 'Nilgün', 'Cansu', 'Melike', 'Tuğba', 'Gizem', 'Hande',
      'Neslihan', 'Burcu', 'Pınar', 'Aslı', 'Begüm', 'Ece', 'Duygu', 'Ceylan', 'Ipek', 'Cemre',
      'Şule', 'Perihan', 'Yasemin', 'Fulya', 'Emel', 'Gül', 'Nevin', 'Sevgi', 'Meral', 'Gönül',
      'Serap', 'Başak', 'Asuman', 'Gülseren', 'İrem', 'Tuba', 'Canan', 'Vildan', 'Nermin', 'Şenay',
      'Hülya', 'Leyla', 'Fadime', 'Figen', 'Gülay', 'Şengül', 'Emine', 'Rukiye', 'Sabiha', 'Safiye',
      'Şerife', 'Aysel', 'Necla', 'Belma', 'Güzin', 'Nesrin', 'Münire', 'Hanife', 'Zehra', 'Güler',
      'İnci', 'Müzeyyen', 'Raziye', 'Sultana', 'Halime', 'Nadire', 'Mediha', 'Fatoş', 'Sevda', 'Nuray',
      'Kadriye', 'Ayten', 'Melahat', 'Fehime', 'Pakize', 'Birgül', 'Ceyda', 'Burçin', 'Defne', 'Müge'
    ];

    // İlgi alanları kategorileri
    const interestCategories = {
      arts: ['Resim', 'Müzik', 'Dans', 'Tiyatro', 'Sinema', 'Fotoğrafçılık', 'Edebiyat', 'Heykel', 'Seramik', 'Gravür'],
      sports: ['Yoga', 'Pilates', 'Yüzme', 'Fitness', 'Koşu', 'Bisiklet', 'Tenis', 'Voleybol', 'Basketbol', 'Futbol', 'Badminton', 'Squash'],
      hobbies: ['Yemek Pişirme', 'Bahçıvanlık', 'El Sanatları', 'Örgü', 'Nakış', 'Seramik', 'Ahşap İşleri', 'Cam Sanatı', 'Mozaik'],
      social: ['Arkadaşlarla Takılmak', 'Sosyal Medya', 'Gönüllülük', 'Networking', 'Topluluk Etkinlikleri', 'Charity Work'],
      travel: ['Seyahat', 'Kamp', 'Doğa Yürüyüşü', 'Tarih', 'Kültür Turları', 'Backpacking', 'Road Trips', 'Cruise'],
      lifestyle: ['Alışveriş', 'Moda', 'Güzellik', 'Dekorasyon', 'Minimalizm', 'DIY Projeleri', 'Vintage Koleksiyonu'],
      learning: ['Dil Öğrenme', 'Okuma', 'Online Kurslar', 'Webinar', 'Seminer', 'Workshop', 'Sertifika Programları'],
      tech: ['Teknoloji', 'Oyun', 'Kodlama', 'Sosyal Medya', 'Blogger', 'Web Tasarım', 'Mobil Uygulama'],
      food: ['Gastronomi', 'Wine Tasting', 'Coffee Culture', 'Healthy Eating', 'Vegan Life', 'Organic Food'],
      nature: ['Bitki Yetiştirme', 'Hayvan Sevgisi', 'Çevre Koruma', 'Sürdürülebilirlik', 'Doğa Fotoğrafçılığı'],
      mindfulness: ['Meditasyon', 'Mindfulness', 'Spirituality', 'Personal Development', 'Life Coaching'],
      entertainment: ['Netflix', 'Podcast', 'Stand-up Comedy', 'Konser', 'Festival', 'Board Games']
    };

    // Biyografi şablonları
    const bioTemplates = [
      "Hayatı dolu dolu yaşayan {age} yaşında bir {profession}. {hobby1} yapmayı ve {hobby2} çok seviyorum. {city}'de yaşıyorum ve yeni insanlarla tanışmayı seviyorum. ✨",
      "{profession} olarak çalışan {age} yaşında biri olarak, boş zamanlarımı {hobby1} ile geçirmeyi tercih ediyorum. {hobby2} de hayatımın ayrılmaz bir parçası. {city}'den selamlar! 🌟",
      "Merhaba! {age} yaşındayım ve {profession} mesleğini icra ediyorum. {hobby1} tutkunu ve {hobby2} seven biriyim. {city}'de güzel anılar biriktiriyorum. 💫",
      "{city}'de yaşayan {age} yaşında bir {profession}. Hayatta en çok {hobby1} ve {hobby2} yapmaktan keyif alıyorum. Pozitif enerjili insanlarla tanışmak istiyorum! 🎈",
      "Yaşam dolu bir {profession} olarak {age} yaşındayım. {hobby1} benim için vazgeçilmez, {hobby2} ise ruhumu dinlendiriyor. {city}'den herkese merhaba! ☀️",
      "{age} yaşında, {city}'li bir {profession}. {hobby1} ve {hobby2} ile dolu bir hayat yaşıyorum. Yeni dostluklar kurmaya hazırım! 🌸",
      "Sevgili {city}'de yaşayan {age} yaşında bir {profession}. {hobby1} tutkusu ve {hobby2} aşkıyla dolu bir ruh. Hayattan zevk almayı seviyorum! 🦋",
      "{profession} mesleğinde {age} yaşında. {city}'de {hobby1} ve {hobby2} ile geçen zamanlarım çok değerli. Keyifli sohbetler için buradayım! 💫",
      "Merhaba! {city}'den {age} yaşında bir {profession}. {hobby1} ve {hobby2} sayesinde hayat çok güzel. Benzer ruhlu insanlarla tanışmak istiyorum! ✨",
      "{age} yaş, {city} şehri, {profession} mesleği... {hobby1} ve {hobby2} ile renklenen bir hayat. Yeni maceralara hazırım! 🌟",
      "Kendini {profession} olarak tanımlayan {age} yaşında biri. {city}'de {hobby1} yaparken ve {hobby2} ile ilgilenirken mutlu oluyorum. 🎭",
      "{city}'de doğup büyüyen {age} yaşında bir {profession}. {hobby1} tutkusu ve {hobby2} sevgisi ile dolu bir kalp. Güzel anılar yaratmaya hazırım! 🎪"
    ];

    // Türkiye şehirleri
    const turkishCities = [
      'İstanbul', 'Ankara', 'İzmir', 'Antalya', 'Bursa', 'Konya', 'Gaziantep', 'Adana',
      'Mersin', 'Kayseri', 'Eskişehir', 'Diyarbakır', 'Samsun', 'Denizli', 'Şanlıurfa',
      'Adapazarı', 'Malatya', 'Kahramanmaraş', 'Erzurum', 'Van', 'Batman', 'Elazığ',
      'İzmit', 'Manisa', 'Sivas', 'Gebze', 'Balıkesir', 'Tarsus', 'Kütahya', 'Trabzon'
    ];

    // Meslekler
    const professions = [
      'Öğretmen', 'Hemşire', 'Mimar', 'Avukat', 'Doktor', 'Mühendis', 'Pazarlama Uzmanı',
      'Grafik Tasarımcı', 'Muhasebeci', 'Psikolog', 'Fizyoterapist', 'Eczacı', 'Diş Hekimi',
      'Veteriner', 'İç Mimar', 'İnsan Kaynakları Uzmanı', 'Sosyal Medya Uzmanı', 'Çevirmen',
      'Emlak Uzmanı', 'Turist Rehberi', 'Kuaför', 'Estetisyen', 'Beslenme Uzmanı', 'Antrenör',
      'Bankacı', 'Sigortacı', 'Satış Temsilcisi', 'Müze Uzmanı', 'Kütüphaneci', 'Gazeteci',
      'Fotoğrafçı', 'Oyuncu', 'Müzisyen', 'Şarkıcı', 'Dansçı', 'Özel Güvenlik', 'Şoför',
      'Aşçı', 'Garson', 'Kasiyer', 'Berber', 'Temizlik Görevlisi', 'Güvenlik Görevlisi',
      'Mağaza Müdürü', 'Proje Yöneticisi', 'İnşaat Mühendisi', 'Bilgisayar Mühendisi',
      'Endüstri Mühendisi', 'Elektrik Mühendisi', 'Makine Mühendisi', 'Jeolog',
      'Biyolog', 'Kimyager', 'Fizikçi', 'Matematikçi', 'İstatistikçi', 'Aktüer',
      'Finans Uzmanı', 'Yatırım Danışmanı', 'Borsa Analisti', 'Risk Uzmanı',
      'Operasyon Uzmanı', 'Lojistik Uzmanı', 'Tedarik Zinciri Uzmanı', 'Kalite Kontrol',
      'Araştırma Görevlisi', 'Öğretim Görevlisi', 'Akademisyen', 'Dekan', 'Müdür'
    ];

    const relationshipStatuses = ['Bekar', 'İlişkisi yok', 'Açık ilişkiye'];
    const languages = ['Türkçe', 'İngilizce', 'Almanca', 'Fransızca', 'İspanyolca', 'İtalyanca'];

    try {
      progressCallback?.(0, total, '🔄 HYBRID: Sahte kullanıcı oluşturma işlemi başlatılıyor...');

      // Kullanılan isimleri takip etmek için set
      const usedNames = new Set<string>();
      const usedBios = new Set<string>();
      const usedInterestCombinations = new Set<string>();

      for (let i = 0; i < total; i++) {
        try {
          // Benzersiz isim seçimi
          let name: string;
          let attempts = 0;
          do {
            const randomIndex = Math.floor(Math.random() * turkishFemaleNames.length);
            name = turkishFemaleNames[randomIndex];
            attempts++;
          } while (usedNames.has(name) && attempts < 50);
          
          if (usedNames.has(name)) {
            // Eğer isim tekrar ediyorsa, sonuna sayı ekle
            name = `${name}${Math.floor(Math.random() * 999) + 1}`;
          }
          usedNames.add(name);

          const age = Math.floor(Math.random() * (38 - 25 + 1)) + 25; // 25-38 yaş arası
          const city = turkishCities[Math.floor(Math.random() * turkishCities.length)];
          const profession = professions[Math.floor(Math.random() * professions.length)];

          // Benzersiz profil fotoğrafı için çeşitli servisler
          // Her kullanıcı için farklı kaynak ve benzersiz parametreler
          const uniquePhotoId = 1000 + i; // 1000-1099 arası benzersiz ID'ler
          const randomSeed = `turkish-woman-${Date.now()}-${i}-${Math.random().toString(36).substr(2, 9)}`;
          
          // Farklı fotoğraf servisleri kullanarak %100 benzersiz garantisi
          let profilePhotoUrl: string;
          const photoServices = [
            `https://picsum.photos/seed/${randomSeed}/400/400`,
            `https://picsum.photos/seed/face${uniquePhotoId}/400/400`,
            `https://randomuser.me/api/portraits/women/${(uniquePhotoId % 99) + 1}.jpg`,
            `https://picsum.photos/seed/woman${uniquePhotoId}face/400/400`
          ];
          
          profilePhotoUrl = photoServices[i % photoServices.length];

          // 3-5 arası rastgele ilgi alanı seçimi - benzersiz kombinasyon garantisi
          const allInterests = Object.values(interestCategories).flat();
          const numberOfInterests = Math.floor(Math.random() * 3) + 3; // 3-5 arası
          let selectedInterests: string[] = [];
          let interestAttempts = 0;
          
          do {
            selectedInterests = [];
            while (selectedInterests.length < numberOfInterests) {
              const randomInterest = allInterests[Math.floor(Math.random() * allInterests.length)];
              if (!selectedInterests.includes(randomInterest)) {
                selectedInterests.push(randomInterest);
              }
            }
            interestAttempts++;
          } while (usedInterestCombinations.has(selectedInterests.sort().join(',')) && interestAttempts < 10);
          
          usedInterestCombinations.add(selectedInterests.sort().join(','));

          // Benzersiz biyografi oluşturma
          let bio: string;
          let bioAttempts = 0;
          do {
            const bioTemplate = bioTemplates[Math.floor(Math.random() * bioTemplates.length)];
            const hobby1 = selectedInterests[0];
            const hobby2 = selectedInterests[1];
            
            bio = bioTemplate
              .replace('{age}', age.toString())
              .replace('{profession}', profession.toLowerCase())
              .replace('{hobby1}', hobby1.toLowerCase())
              .replace('{hobby2}', hobby2.toLowerCase())
              .replace('{city}', city);
            bioAttempts++;
          } while (usedBios.has(bio) && bioAttempts < 5);
          
          usedBios.add(bio);

          // 2-4 arası rastgele dil seçimi
          const numberOfLanguages = Math.floor(Math.random() * 3) + 2; // 2-4 arası
          const selectedLanguages: string[] = ['Türkçe']; // Türkçe her zaman dahil
          
          while (selectedLanguages.length < numberOfLanguages) {
            const randomLanguage = languages[Math.floor(Math.random() * languages.length)];
            if (!selectedLanguages.includes(randomLanguage)) {
              selectedLanguages.push(randomLanguage);
            }
          }

          // Rastgele konum (Türkiye sınırları içinde)
          const latitude = 36 + Math.random() * (42 - 36); // 36-42 arası enlem
          const longitude = 26 + Math.random() * (45 - 26); // 26-45 arası boylam

          // Benzersiz email adresi oluşturma
          const cleanName = name.toLowerCase().replace(/ş/g, 's').replace(/ç/g, 'c').replace(/ğ/g, 'g').replace(/ü/g, 'u').replace(/ı/g, 'i').replace(/ö/g, 'o');

          // ✅ birthDate hesaplama (Flutter uyumlu)
          const currentYear = new Date().getFullYear();
          const birthYear = currentYear - age;
          const birthDate = new Date(birthYear, Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1);

          // 🆕 İlgilendiği cinsiyet dağılımı (kadın kullanıcılar için)
          let interestedIn: string[];
          const randomChance = Math.random();
          if (randomChance < 0.8) {
            // %80 erkeklerle ilgilenir (heteroseksüel)
            interestedIn = ['Erkek'];
          } else {
            // %20 kadınlarla ilgilenir (lezbiyen)
            interestedIn = ['Kadın'];
          }

          const userData = {
            name: `${name}`,  // surname kaldırıldı çünkü create100FakeTurkishWomen'da surname yok
            email: `test${i + 1001}.${cleanName}@gmail.com`,
            age: age,
            birthDate: birthDate, // ✅ Added birthDate for Flutter compatibility
            gender: 'Kadın', // ✅ DÜZELT: Kadın kullanıcıları için doğru gender
            interestedIn: interestedIn, // ✅ DÜZELT: Kadın mantığına göre cinsiyet tercihi
            bio: `Merhaba! Ben ${name}. ${age} yaşındayım ve İstanbul'da yaşıyorum. ${selectedInterests.slice(0, 2).join(' ve ')} ile ilgileniyorum.`, // userInterests yerine selectedInterests
            interests: selectedInterests,
            relationshipStatus: relationshipStatuses[Math.floor(Math.random() * relationshipStatuses.length)],
            languages: selectedLanguages,
            photos: [profilePhotoUrl],
            profilePicUrl: profilePhotoUrl, // ✅ ADDED: Flutter için ana profil resmi field'ı
            location: {
              city: city,
              latitude: latitude,
              longitude: longitude
            },
            isActive: true,
            createdAt: new Date(),
            lastActive: new Date() // ✅ Fixed: Use lastActive for Flutter compatibility
          };

          // 🔄 HYBRID: Önce Supabase'e eklemeyi dene
          let userCreated = false;
          let method = '';

          if (useSupabase && supabaseService) {
            try {
              // Supabase user data format (id'yi de ekle)
              const supabaseUserData = {
                id: `user_${Date.now()}_${i}`, // Benzersiz ID
                ...userData,
                created_at: new Date().toISOString(),
                last_active: new Date().toISOString(),
                is_active: true,
                profile_pic_url: profilePhotoUrl,
                birth_date: birthDate.toISOString(),
                interested_in: interestedIn,
                relationship_status: userData.relationshipStatus
              };

              // Supabase'e kullanıcı ekleme fonksiyonu (dinamik olarak çağırıyoruz)
              const supabaseResult = await supabaseService.createUser?.(supabaseUserData);
              
              if (supabaseResult) {
                userCreated = true;
                method = 'Supabase';
                progressCallback?.(i + 1, total, `✅ HYBRID: ${name} (${age}) Supabase'e oluşturuldu - ${city} | İlgi: ${selectedInterests.slice(0, 2).join(', ')}`);
              } else {
                throw new Error('Supabase createUser false döndü');
              }
            } catch (supabaseError) {
              console.log(`⚠️ HYBRID: Kullanıcı ${i + 1} Supabase hatası, Firebase fallback...`, supabaseError);
              useSupabase = false; // Supabase'i devre dışı bırak
            }
          }

          // 🔄 HYBRID: Supabase başarısız olduysa Firebase'e ekle
          if (!userCreated) {
          await addDoc(collection(db, 'users'), userData);
            userCreated = true;
            method = 'Firebase';
            progressCallback?.(i + 1, total, `✅ HYBRID: ${name} (${age}) Firebase'e oluşturuldu - ${city} | İlgi: ${selectedInterests.slice(0, 2).join(', ')} [${method}]`);
          }

          if (userCreated) {
          createdCount++;
          }

          // Rate limiting için kısa bekleme
          if (i % 10 === 9) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }

        } catch (error) {
          errorCount++;
          console.error(`Kullanıcı ${i + 1} oluşturulurken hata:`, error);
          progressCallback?.(i + 1, total, `❌ Kullanıcı ${i + 1} oluşturulamadı: ${error}`);
        }
      }

      const message = `${createdCount} kullanıcı başarıyla oluşturuldu, ${errorCount} hata oluştu.`;
      progressCallback?.(total, total, `🎉 HYBRID: Tamamlandı: ${message}`);

      return {
        success: true,
        createdCount,
        errorCount,
        message
      };

    } catch (error: any) {
      console.error('Fake user creation batch error:', error);
      return {
        success: false,
        createdCount,
        errorCount: errorCount + 1,
        message: `Kritik hata: ${error.message}`
      };
    }
  },

  // Fake kullanıcıları getir (test email domainleri olanlar)
  async getFakeUsers() {
    try {
      console.log('🔍 Fake kullanıcılar getiriliyor...');
      
      // Tüm kullanıcıları getir
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const fakeUsers: AppUser[] = [];
      
      usersSnapshot.forEach((doc) => {
        const userData = doc.data();
        const email = userData.email || '';
        
        // Email'in test formatında olup olmadığını kontrol et (test1., test2. ile başlayanlar)
        const isFakeUser = email.includes('@gmail.com') && /test\d+\./.test(email);
        
        if (isFakeUser) {
          fakeUsers.push({
            id: doc.id,
            email: userData.email || '',
            name: userData.name || '',
            age: userData.age || 18,
            gender: userData.gender || '',
            isActive: userData.isActive !== false,
            createdAt: userData.createdAt,
            lastActiveAt: userData.lastActiveAt,
            photos: userData.photos || [],
            bio: userData.bio || '',
            interests: userData.interests || [],
            relationshipStatus: userData.relationshipStatus || '',
            languages: userData.languages || [],
            photoURL: userData.photoURL || null,
            profilePictureUrl: userData.profilePictureUrl || null,
            profilePicUrl: userData.profilePicUrl || null,
            googlePhotoURL: userData.googlePhotoURL || null
          });
        }
      });
      
      // Oluşturulma tarihine göre sırala (en yeni önce)
      fakeUsers.sort((a, b) => {
        const dateA = a.createdAt;
        const dateB = b.createdAt;
        
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        
        let timeA, timeB;
        
        try {
          timeA = dateA.toDate ? dateA.toDate().getTime() : new Date(dateA).getTime();
        } catch {
          timeA = 0;
        }
        
        try {
          timeB = dateB.toDate ? dateB.toDate().getTime() : new Date(dateB).getTime();
        } catch {
          timeB = 0;
        }
        
        return timeB - timeA;
      });
      
      console.log(`✅ ${fakeUsers.length} fake kullanıcı bulundu`);
      return fakeUsers;
    } catch (error) {
      console.error('Fake kullanıcıları getirme hatası:', error);
      throw error;
    }
  },

  // 100 Türk erkek kullanıcı oluştur
  async create100FakeTurkishMen(
    progressCallback?: (current: number, total: number, message: string) => void
  ): Promise<{ success: boolean; createdCount: number; errorCount: number; message: string }> {
    try {
      console.log('🔄 HYBRID: 100 Türk erkek kullanıcı oluşturuluyor...');
      progressCallback?.(0, 100, '🚀 HYBRID: 100 Türk erkek kullanıcı oluşturma işlemi başlatıldı');

      // 🔄 HYBRID: Supabase service'ini import et
      let supabaseService: any = null;
      let useSupabase = true;
      
      try {
        const imported = await import('./supabaseService');
        supabaseService = imported.supabaseService;
        console.log('✅ HYBRID: Supabase service başarıyla yüklendi');
      } catch (importError) {
        console.log('⚠️ HYBRID: Supabase service yüklenemedi, sadece Firebase kullanılacak:', importError);
        useSupabase = false;
      }

      const turkishMaleNames = [
        'Ahmet', 'Mehmet', 'Mustafa', 'Ali', 'Hasan', 'Hüseyin', 'İbrahim', 'İsmail', 'Ömer', 'Yusuf',
        'Emre', 'Burak', 'Cem', 'Deniz', 'Ege', 'Furkan', 'Gökhan', 'Halil', 'Kerem', 'Murat',
        'Okan', 'Onur', 'Özgür', 'Serkan', 'Sinan', 'Taner', 'Tolga', 'Umut', 'Volkan', 'Yalçın',
        'Barış', 'Berk', 'Can', 'Arda', 'Kaan', 'Taylan', 'Erkan', 'Fatih', 'Selim', 'Tarık',
        'Alper', 'Bora', 'Enes', 'Eren', 'Erhan', 'Kağan', 'Koray', 'Levent', 'Oğuz', 'Serdar'
      ];

      const surnames = [
        'Yılmaz', 'Kaya', 'Demir', 'Şahin', 'Çelik', 'Aydın', 'Özkan', 'Arslan', 'Doğan', 'Kılıç',
        'Aslan', 'Çetin', 'Kara', 'Koç', 'Kurt', 'Özdemir', 'Şen', 'Türk', 'Uçar', 'Yıldız'
      ];

      const interests = [
        'Futbol', 'Teknoloji', 'Müzik', 'Seyahat', 'Fitness', 'Oyun', 'Kitap Okuma', 'Sinema',
        'Fotoğrafçılık', 'Doğa Yürüyüşü', 'Basketbol', 'Motor', 'Bilgisayar', 'Kahve', 'Sanat',
        'Tarihi Yerler', 'Kampçılık', 'Balık Tutma', 'Müze', 'Konsere Gitme'
      ];

      const relationshipStatuses = ['single', 'relationship', 'complicated'];
      const languages = ['Türkçe', 'İngilizce'];

      // İstanbul koordinatları
      const istanbulBounds = {
        north: 41.2,
        south: 40.8,
        east: 29.2,
        west: 28.6
      };

      let createdCount = 0;
      let errorCount = 0;

      for (let i = 0; i < 100; i++) {
        try {
          // Rastgele isim ve soyisim seç
          const name = turkishMaleNames[Math.floor(Math.random() * turkishMaleNames.length)];
          const surname = surnames[Math.floor(Math.random() * surnames.length)]; // 🆕 surname tanımlaması eklendi
          const age = Math.floor(Math.random() * 12) + 24; // 24-35 yaş arası

          // Rastgele şehir ve koordinat
          const city = 'İstanbul';
          const latitude = istanbulBounds.south + Math.random() * (istanbulBounds.north - istanbulBounds.south);
          const longitude = istanbulBounds.west + Math.random() * (istanbulBounds.east - istanbulBounds.west);

          // Rastgele ilgi alanları (2-4 tane)
          const interestCount = Math.floor(Math.random() * 3) + 2;
          const selectedInterests = interests.sort(() => 0.5 - Math.random()).slice(0, interestCount);

          // Rastgele diller (1-2 tane)
          const languageCount = Math.floor(Math.random() * 2) + 1;
          const selectedLanguages = languages.sort(() => 0.5 - Math.random()).slice(0, languageCount);

          // Doğum tarihi hesapla (flutter için)
          const birthDate = new Date();
          birthDate.setFullYear(birthDate.getFullYear() - age);

          // Profil resmi URL'si (erkek için)
          const cleanName = name.toLowerCase().replace(/[^a-z]/g, '');
          const profilePhotoUrl = `https://i.pravatar.cc/300?img=${Math.floor(Math.random() * 70) + 1}`;

          // %80 kadınlarla ilgilenir (heteroseksüel), %20 erkeklerle ilgilenir (homoseksüel) 
          let interestedIn: string[];
          if (Math.random() < 0.8) {
            // %80 kadınlarla ilgilenir (heteroseksüel)
            interestedIn = ['Kadın'];
          } else {
            // %20 erkeklerle ilgilenir (homoseksüel)
            interestedIn = ['Erkek'];
          }

          const userData = {
            name: `${name} ${surname}`,
            email: `test${i + 1001}.${cleanName}@gmail.com`,
            age: age,
            birthDate: birthDate, // ✅ Added birthDate for Flutter compatibility
            gender: 'Erkek', // Türkçe format (Flutter uyumlu)
            interestedIn: interestedIn, // 🆝 Dinamik cinsiyet tercihi
            bio: `Merhaba! Ben ${name}. ${age} yaşındayım ve İstanbul'da yaşıyorum. ${selectedInterests.slice(0, 2).join(' ve ')} ile ilgileniyorum.`, // 🆕 selectedInterests kullanıldı
            interests: selectedInterests,
            relationshipStatus: relationshipStatuses[Math.floor(Math.random() * relationshipStatuses.length)],
            languages: selectedLanguages,
            photos: [profilePhotoUrl],
            profilePicUrl: profilePhotoUrl, // ✅ ADDED: Flutter için ana profil resmi field'ı
            location: {
              city: city,
              latitude: latitude,
              longitude: longitude
            },
            isActive: true,
            createdAt: new Date(),
            lastActive: new Date() // ✅ Fixed: Use lastActive for Flutter compatibility
          };

          // 🔄 HYBRID: Önce Supabase'e eklemeyi dene
          let userCreated = false;
          let method = '';

          if (useSupabase && supabaseService) {
            try {
              // Supabase user data format (id'yi de ekle)
              const supabaseUserData = {
                id: `user_${Date.now()}_${i}`, // Benzersiz ID
                ...userData,
                created_at: new Date().toISOString(),
                last_active: new Date().toISOString(),
                is_active: true,
                profile_pic_url: profilePhotoUrl,
                birth_date: birthDate.toISOString(),
                interested_in: interestedIn,
                relationship_status: userData.relationshipStatus
              };

              // Supabase'e kullanıcı ekleme fonksiyonu (dinamik olarak çağırıyoruz)
              const supabaseResult = await supabaseService.createUser?.(supabaseUserData);
              
              if (supabaseResult) {
                userCreated = true;
                method = 'Supabase';
                progressCallback?.(i + 1, 100, `✅ HYBRID: ${name} ${surname} Supabase'e oluşturuldu (${i + 1}/100)`);
              } else {
                throw new Error('Supabase createUser false döndü');
              }
            } catch (supabaseError) {
              console.log(`⚠️ HYBRID: Kullanıcı ${i + 1} Supabase hatası, Firebase fallback...`, supabaseError);
              useSupabase = false; // Supabase'i devre dışı bırak
            }
          }

          // 🔄 HYBRID: Supabase başarısız olduysa Firebase'e ekle
          if (!userCreated) {
          await addDoc(collection(db, 'users'), userData);
            userCreated = true;
            method = 'Firebase';
            progressCallback?.(i + 1, 100, `✅ HYBRID: ${name} ${surname} Firebase'e oluşturuldu (${i + 1}/100) [${method}]`);
          }

          if (userCreated) {
          createdCount++;
          }

          // Her 10 kullanıcıda bir kısa bekleme
          if ((i + 1) % 10 === 0) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }

        } catch (error) {
          errorCount++;
          console.error(`Erkek kullanıcı ${i + 1} oluşturulamadı:`, error);
          progressCallback?.(i + 1, 100, `❌ Kullanıcı ${i + 1} oluşturulamadı: ${error}`);
        }
      }

      const message = `${createdCount} erkek kullanıcı başarıyla oluşturuldu. ${errorCount} hata.`;
      console.log('✅ HYBRID: Erkek kullanıcı oluşturma tamamlandı:', message);
      
      progressCallback?.(100, 100, `🎉 HYBRID: İşlem tamamlandı! ${createdCount} erkek kullanıcı oluşturuldu`);

      return {
        success: true,
        createdCount,
        errorCount,
        message
      };

    } catch (error) {
      console.error('HYBRID: Erkek kullanıcı oluşturma hatası:', error);
      const errorMessage = error instanceof Error ? error.message : 'Bilinmeyen hata';
      
      return {
        success: false,
        createdCount: 0,
        errorCount: 100,
        message: `Kritik hata: ${errorMessage}`
      };
    }
  },

  // Admin kullanıcının preferences'ını analiz et
  async analyzeAdminUserPreferences() {
    try {
      console.log('🔍 Admin kullanıcının preferences analizi başlatılıyor...');
      
      // Önce email ile admin kullanıcıyı bul
      const adminQuery = query(
        collection(db, 'users'),
        where('email', '==', 'datezy@admin.com')
      );
      
      const adminSnapshot = await getDocs(adminQuery);
      
      if (!adminSnapshot.empty) {
        const adminDoc = adminSnapshot.docs[0];
        const adminData = adminDoc.data();
        const realAdminId = adminDoc.id;
        
        console.log('👤 Admin kullanıcı bilgileri:');
        console.log('  - Real ID:', realAdminId);
        console.log('  - Name:', adminData.name);
        console.log('  - Email:', adminData.email);
        console.log('  - Gender:', adminData.gender);
        console.log('  - InterestedIn:', adminData.interestedIn);
        console.log('  - InterestedIn Type:', typeof adminData.interestedIn);
        console.log('  - InterestedIn Array?:', Array.isArray(adminData.interestedIn));
        console.log('  - Age:', adminData.age);
        
        return {
          exists: true,
          data: {
            id: realAdminId,
            name: adminData.name,
            email: adminData.email,
            gender: adminData.gender,
            interestedIn: adminData.interestedIn,
            interestedInType: typeof adminData.interestedIn,
            interestedInIsArray: Array.isArray(adminData.interestedIn),
            age: adminData.age
          }
        };
      } else {
        console.log('❌ datezy@admin.com email ile admin kullanıcı bulunamadı');
        
        // Alternatif: Z02SfMSJRLf6a06nhJDqd5AoOPk1 ID ile dene
        const adminDoc = await getDoc(doc(db, 'users', 'Z02SfMSJRLf6a06nhJDqd5AoOPk1'));
        
        if (adminDoc.exists()) {
          const adminData = adminDoc.data();
          console.log('👤 ID ile bulunan admin kullanıcı:');
          console.log('  - ID:', adminDoc.id);
          console.log('  - Name:', adminData.name);
          console.log('  - Email:', adminData.email);
          console.log('  - Gender:', adminData.gender);
          console.log('  - InterestedIn:', adminData.interestedIn);
          console.log('  - InterestedIn Type:', typeof adminData.interestedIn);
          console.log('  - InterestedIn Array?:', Array.isArray(adminData.interestedIn));
          
          return {
            exists: true,
            data: {
              id: adminDoc.id,
              name: adminData.name,
              email: adminData.email,
              gender: adminData.gender,
              interestedIn: adminData.interestedIn,
              interestedInType: typeof adminData.interestedIn,
              interestedInIsArray: Array.isArray(adminData.interestedIn)
            }
          };
        } else {
          console.log('❌ Hiçbir yöntemle admin kullanıcı bulunamadı');
          return { exists: false };
        }
      }
    } catch (error) {
      console.error('Admin preferences analiz hatası:', error);
      return { exists: false, error: error };
    }
  },

  // Admin kullanıcının preferences'ını güncelle (her iki cinsiyeti de görebilsin)
  async updateAdminUserPreferences() {
    try {
      console.log('🔧 Admin kullanıcının preferences\'ı güncelleniyor...');
      
      // Önce admin kullanıcıyı bul
      const adminQuery = query(
        collection(db, 'users'),
        where('email', '==', 'datezy@admin.com')
      );
      
      const adminSnapshot = await getDocs(adminQuery);
      let adminDocRef;
      
      if (!adminSnapshot.empty) {
        adminDocRef = adminSnapshot.docs[0].ref;
      } else {
        // Alternatif ID ile dene
        const adminDoc = await getDoc(doc(db, 'users', 'Z02SfMSJRLf6a06nhJDqd5AoOPk1'));
        if (adminDoc.exists()) {
          adminDocRef = adminDoc.ref;
        } else {
          throw new Error('Admin kullanıcı bulunamadı');
        }
      }
      
      // Preferences'ı güncelle
      await updateDoc(adminDocRef, {
        gender: 'Erkek', // Flutter uyumlu format
        interestedIn: ['Kadın', 'Erkek'], // Hem kadın hem erkek kullanıcıları görebilsin
        updatedAt: new Date()
      });
      
      console.log('✅ Admin kullanıcının preferences\'ı güncellendi: gender=Erkek, interestedIn=[Kadın, Erkek]');
      return true;
    } catch (error) {
      console.error('❌ Admin preferences güncelleme hatası:', error);
      throw error;
    }
  },

  // Son oluşturulan erkek kullanıcıları analiz et
  async analyzeRecentMaleUsers(count: number = 5) {
    try {
      console.log('🔍 Son oluşturulan erkek kullanıcılar analiz ediliyor...');
      
      const usersQuery = query(
        collection(db, 'users'),
        where('gender', '==', 'Erkek'), // Türkçe format
        orderBy('createdAt', 'desc'),
        limit(count)
      );
      
      const snapshot = await getDocs(usersQuery);
      const maleUsers: any[] = [];
      
      snapshot.forEach(doc => {
        const userData = doc.data();
        maleUsers.push({
          id: doc.id,
          name: userData.name,
          email: userData.email,
          gender: userData.gender,
          interestedIn: userData.interestedIn,
          age: userData.age,
          createdAt: userData.createdAt
        });
      });
      
      console.log(`👨 Son ${maleUsers.length} erkek kullanıcı:`, maleUsers);
      return maleUsers;
    } catch (error) {
      console.error('Erkek kullanıcı analiz hatası:', error);
      return [];
    }
  },

  // 🆕 FCM token'ı boş olan kullanıcılara benzersiz FCM token ekleme
  async assignFCMTokensToEmptyUsers(callback?: (current: number, total: number, message: string) => void) {
    try {
      console.log('🔄 FCM token\'ı boş olan kullanıcılar aranıyor...');
      callback?.(0, 100, '🔍 FCM token\'ı boş olan kullanıcılar aranıyor...');
      
      // FCM token'ı boş olan kullanıcıları bul
      const usersWithoutTokenQuery = query(
        collection(db, 'users'),
        where('fcmToken', 'in', [null, '', undefined]),
        limit(500) // Güvenlik için limit koy
      );
      
      const usersSnapshot = await getDocs(usersWithoutTokenQuery);
      const totalUsers = usersSnapshot.size;
      
      if (totalUsers === 0) {
        const message = '✅ FCM token\'ı boş olan kullanıcı bulunamadı!';
        console.log(message);
        callback?.(100, 100, message);
        return {
          success: true,
          updatedCount: 0,
          totalCount: 0,
          message: 'Güncellenecek kullanıcı bulunamadı'
        };
      }
      
      console.log(`📊 ${totalUsers} kullanıcının FCM token'ı boş/eksik`);
      callback?.(10, 100, `📊 ${totalUsers} kullanıcının FCM token'ı boş/eksik bulundu`);
      
      let updatedCount = 0;
      let errorCount = 0;
      const usedTokens = new Set<string>(); // Çakışma önleme için
      
      const applyTokenUpdate = async (
        userDoc: any,
        userData: any,
        token: string,
        index: number
      ) => {
        try {
          await updateDoc(userDoc.ref, {
            fcmToken: token,
            fcmTokenUpdatedAt: new Date(),
            fcmTokenSource: 'admin_auto_assigned'
          });
          updatedCount++;
          const progress = Math.round(((index + 1) / totalUsers) * 80) + 20; // %20'den %100'e
          callback?.(progress, 100, `✅ ${userData.name || 'İsimsiz'} kullanıcısına FCM token atandı (${updatedCount}/${totalUsers})`);
        } catch (error) {
          errorCount++;
          console.error(`Kullanıcı ${userDoc.id} güncellenirken hata:`, error);
          callback?.(0, 100, `❌ ${userData.name || 'İsimsiz'} kullanıcısı güncellenirken hata`);
        }
      };

      // Batch işlem için array
      const updates: Promise<void>[] = [];
      
      for (let i = 0; i < usersSnapshot.docs.length; i++) {
        const userDoc = usersSnapshot.docs[i];
        const userData = userDoc.data();
        
        try {
          // Benzersiz FCM token oluştur
          let uniqueToken: string;
          let attempts = 0;
          
          do {
            // Gerçek FCM token formatına benzer benzersiz token oluştur
            const randomId = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
            const timestamp = Date.now().toString(36);
            const userIdPart = userDoc.id.substring(0, 8);
            uniqueToken = `fcm_${userIdPart}_${timestamp}_${randomId}:APA91bE${Math.random().toString(36).substring(2, 30)}`;
            attempts++;
          } while (usedTokens.has(uniqueToken) && attempts < 10);
          
          if (usedTokens.has(uniqueToken)) {
            throw new Error('Benzersiz token oluşturulamadı');
          }
          
          usedTokens.add(uniqueToken);
          
          // Kullanıcıyı güncelle
          updates.push(applyTokenUpdate(userDoc, userData, uniqueToken, i));
          
          // Her 50 işlemde bir bekle (rate limiting)
          if (updates.length >= 50) {
            await Promise.all(updates);
            updates.length = 0; // Array'i temizle
            await new Promise(resolve => setTimeout(resolve, 1000)); // 1 saniye bekle
          }
          
        } catch (error) {
          errorCount++;
          console.error(`Token oluşturma hatası kullanıcı ${userDoc.id}:`, error);
          callback?.(0, 100, `❌ ${userData.name || 'İsimsiz'} için token oluşturulamadı`);
        }
      }
      
      // Kalan güncellemeleri tamamla
      if (updates.length > 0) {
        await Promise.all(updates);
      }
      
      const finalMessage = `✅ İşlem tamamlandı! ${updatedCount} kullanıcıya FCM token atandı, ${errorCount} hata`;
      console.log(finalMessage);
      callback?.(100, 100, finalMessage);
      
      return {
        success: true,
        updatedCount,
        errorCount,
        totalCount: totalUsers,
        message: finalMessage
      };
      
    } catch (error) {
      const errorMessage = `❌ FCM token atama hatası: ${error}`;
      console.error(errorMessage);
      callback?.(0, 100, errorMessage);
      throw error;
    }
  }
};

// Hediye kartı servisleri
export const giftCardService = {
  // Tüm hediye kartı başvurularını getir
  async getAllGiftCardApplications(limitCount: number = 50) {
    try {
      const applicationsQuery = query(
        collection(db, 'gift_card_applications'),
        orderBy('applicationDate', 'desc'),
        limit(limitCount)
      );

      const snapshot = await getDocs(applicationsQuery);
      const applications: any[] = [];

      for (const docSnap of snapshot.docs) {
        const appData = docSnap.data();
        
        // Kullanıcı bilgilerini getir
        let userDetails: { name: string; email: string; totalTokens: number; } | undefined = undefined;
        if (appData.userId) {
          try {
            const userDoc = await getDoc(doc(db, 'users', appData.userId));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              userDetails = {
                name: userData.name || 'Bilinmiyor',
                email: userData.email || 'Bilinmiyor',
                totalTokens: userData.totalTokens || 0
              };
            }
          } catch (userError) {
            console.error('Kullanıcı bilgisi getirme hatası:', userError);
          }
        }

        applications.push({
          id: docSnap.id,
          userId: appData.userId || '',
          userName: appData.userName || '',
          userEmail: appData.userEmail || '',
          giftCardType: appData.giftCardType || '',
          giftCardValue: appData.giftCardValue || 0,
          requiredTokens: appData.requiredTokens || 0,
          recipientEmail: appData.recipientEmail || '',
          description: appData.description || '',
          status: appData.status || 'pending', // pending, approved, rejected, sent
          applicationDate: appData.applicationDate,
          createdAt: appData.createdAt,
          adminNotes: appData.adminNotes || '',
          userDetails
        });
      }

      return applications;
    } catch (error) {
      console.error('Hediye kartı başvurularını getirme hatası:', error);
      throw error;
    }
  },

  // Hediye kartı başvuru durumunu güncelle
  async updateGiftCardApplicationStatus(applicationId: string, status: 'approved' | 'rejected' | 'sent', adminNotes?: string) {
    try {
      const appRef = doc(db, 'gift_card_applications', applicationId);
      const updateData: any = { 
        status,
        updatedAt: Timestamp.now()
      };
      
      if (adminNotes) {
        updateData.adminNotes = adminNotes;
      }

      // Eğer onaylandıysa kullanıcının jetonlarını düş
      if (status === 'approved') {
        const appDoc = await getDoc(appRef);
        if (appDoc.exists()) {
          const appData = appDoc.data();
          const userId = appData.userId;
          const requiredTokens = appData.requiredTokens || 0;
          
          if (userId && requiredTokens > 0) {
            try {
              const userRef = doc(db, 'users', userId);
              const userDoc = await getDoc(userRef);
              
              if (userDoc.exists()) {
                const userData = userDoc.data();
                const currentTokens = userData.totalTokens || 0;
                const newTokens = Math.max(0, currentTokens - requiredTokens);
                
                await updateDoc(userRef, { totalTokens: newTokens });
                console.log(`Kullanıcının jetonu güncellendi: ${currentTokens} -> ${newTokens}`);
              }
            } catch (tokenError) {
              console.error('Jeton güncelleme hatası:', tokenError);
            }
          }
        }
      }

      await updateDoc(appRef, updateData);
      return true;
    } catch (error) {
      console.error('Hediye kartı başvuru durumu güncelleme hatası:', error);
      throw error;
    }
  },

  // Bekleyen hediye kartı başvuru sayısı
  async getPendingGiftCardApplicationsCount() {
    try {
      const pendingQuery = query(
        collection(db, 'gift_card_applications'),
        where('status', '==', 'pending')
      );
      const snapshot = await getDocs(pendingQuery);
      return snapshot.size;
    } catch (error) {
      console.error('Bekleyen hediye kartı başvuru sayısı hatası:', error);
      return 0;
    }
  }
};

// Para kazanma servisleri
export const earningService = {
  // Tüm başvuruları getir
  async getAllApplications(limitCount: number = 50) {
    try {
      const applicationsQuery = query(
        collection(db, 'earning_applications'),
        orderBy('applicationDate', 'desc'),
        limit(limitCount)
      );

      const snapshot = await getDocs(applicationsQuery);
      const applications: EarningApplication[] = [];

      for (const docSnap of snapshot.docs) {
        const appData = docSnap.data();
        
        // Kullanıcı bilgilerini getir
        let userDetails: { name: string; email: string; totalEarnings: number; } | undefined = undefined;
        if (appData.userId) {
          try {
            const userDoc = await getDoc(doc(db, 'users', appData.userId));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              userDetails = {
                name: userData.name || 'Bilinmiyor',
                email: userData.email || 'Bilinmiyor',
                totalEarnings: userData.totalEarnings || 0
              };
            }
          } catch (userError) {
            console.error('Kullanıcı bilgisi getirme hatası:', userError);
          }
        }

        applications.push({
          id: docSnap.id,
          userId: appData.userId || '',
          amount: appData.amount || 0,
          status: appData.status || 'pending',
          applicationDate: appData.applicationDate,
          adminNotes: appData.adminNotes || '',
          userDetails
        });
      }

      return applications;
    } catch (error) {
      console.error('Başvuruları getirme hatası:', error);
      throw error;
    }
  },

  // Başvuru durumunu güncelle
  async updateApplicationStatus(applicationId: string, status: 'approved' | 'rejected', adminNotes?: string) {
    try {
      const appRef = doc(db, 'earning_applications', applicationId);
      const updateData: any = { 
        status,
        updatedAt: Timestamp.now()
      };
      
      if (adminNotes) {
        updateData.adminNotes = adminNotes;
      }

      await updateDoc(appRef, updateData);
      return true;
    } catch (error) {
      console.error('Başvuru durumu güncelleme hatası:', error);
      throw error;
    }
  },

  // Bekleyen başvuru sayısı
  async getPendingApplicationsCount() {
    try {
      const pendingQuery = query(
        collection(db, 'earning_applications'),
        where('status', '==', 'pending')
      );
      const snapshot = await getDocs(pendingQuery);
      return snapshot.size;
    } catch (error) {
      console.error('Bekleyen başvuru sayısı hatası:', error);
      return 0;
    }
  }
};

// Rapor servisleri
export const reportService = {
  // Tüm raporları getir
  async getAllReports(limitCount: number = 50) {
    try {
      const reportsQuery = query(
        collection(db, 'user_reports'),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );

      const snapshot = await getDocs(reportsQuery);
      const reports: UserReport[] = [];

      snapshot.forEach((doc) => {
        const reportData = doc.data();
        reports.push({
          id: doc.id,
          reporterId: reportData.reporterId || '',
          reportedUserId: reportData.reportedUserId || '',
          reason: reportData.reason || '',
          description: reportData.description || '',
          status: reportData.status || 'pending',
          createdAt: reportData.createdAt,
          adminNotes: reportData.adminNotes || ''
        });
      });

      return reports;
    } catch (error) {
      console.error('Raporları getirme hatası:', error);
      throw error;
    }
  },

  // Rapor durumunu güncelle
  async updateReportStatus(reportId: string, status: 'reviewing' | 'resolved' | 'dismissed', adminNotes?: string) {
    try {
      const reportRef = doc(db, 'user_reports', reportId);
      const updateData: any = { 
        status,
        updatedAt: Timestamp.now()
      };
      
      if (adminNotes) {
        updateData.adminNotes = adminNotes;
      }

      await updateDoc(reportRef, updateData);
      return true;
    } catch (error) {
      console.error('Rapor durumu güncelleme hatası:', error);
      throw error;
    }
  }
};

  // Dashboard istatistikleri
export const dashboardService = {
  // Toplam eşleşme sayısını getir
  async getTotalMatchesCount() {
    try {
      console.log('📊 Toplam eşleşme sayısı hesaplanıyor...');
      
      const matchesSnapshot = await getDocs(collection(db, 'matches'));
      const totalMatches = matchesSnapshot.size;
      
      console.log('💕 Toplam eşleşme sayısı:', totalMatches);
      return totalMatches;
    } catch (error) {
      console.error('Toplam eşleşme sayısı hatası:', error);
      return 0;
    }
  },

  async getDashboardStats() {
    try {
      const [
        totalUsers,
        activeUsers,
        todayNewUsers,
        pendingApplications,
        pendingGiftCardApplications,
        premiumUsers,
        dailyRevenue,
        monthlyRevenue,
        totalRevenue
      ] = await Promise.all([
        userService.getTotalUsersCount(),
        userService.getActiveUsersCount(),
        userService.getTodayNewUsers(),
        earningService.getPendingApplicationsCount(),
        giftCardService.getPendingGiftCardApplicationsCount(),
        this.getPremiumUsersCount(),
        salesService.getDailyRevenue(), // ✅ Günlük gelir
        salesService.getMonthlyRevenue(), // ✅ Aylık gelir
        salesService.getTotalRevenue() // ✅ Toplam gelir
      ]);

      return {
        totalUsers,
        activeUsers,
        newUsersToday: todayNewUsers,
        pendingApplications,
        pendingGiftCardApplications,
        premiumUsers,
        dailyRevenue, // ✅ Günlük gelir
        monthlyRevenue, // ✅ Aylık gelir
        totalRevenue // ✅ Toplam gelir
      };
    } catch (error) {
      console.error('Dashboard istatistikleri hatası:', error);
      throw error;
    }
  },

  // Premium kullanıcı sayısını getir (subscriptions collection'ından)
  async getPremiumUsersCount() {
    try {
      console.log('🔍 Dashboard premium kullanıcı sayısı hesaplanıyor...');
      
      const subscriptionsQuery = query(
        collection(db, 'subscriptions'),
        where('status', '==', 'active')
      );
      const snapshot = await getDocs(subscriptionsQuery);
      
      console.log('📊 Toplam aktif subscription sayısı:', snapshot.size);
      
      // Premium abonelikleri say (planType ve price alanı olanlar)
      let premiumCount = 0;
      const uniqueUsers = new Set();
      
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        
        // Eğer planType ve price varsa, bu premium abonelik
        if (data.planType && data.price && data.price > 0 && data.subscriberId) {
          // Aynı kullanıcının birden fazla premium aboneliği olmasın diye Set kullan
          uniqueUsers.add(data.subscriberId);
        }
      });
      
      premiumCount = uniqueUsers.size;
      console.log('💎 Unique premium kullanıcı sayısı:', premiumCount);
      
      return premiumCount;
    } catch (error) {
      console.error('Premium kullanıcı sayısı hatası:', error);
      return 0;
    }
  },

  // Son kayıt olan kullanıcıları getir
  async getRecentUsers(count: number = 5) {
    try {
      const recentQuery = query(
        collection(db, 'users'),
        orderBy('createdAt', 'desc'),
        limit(count)
      );

      const snapshot = await getDocs(recentQuery);
      const users: AppUser[] = [];

      snapshot.forEach((doc) => {
        const userData = doc.data();
        users.push({
          id: doc.id,
          email: userData.email || '',
          name: userData.name || '',
          age: userData.age || 18,
          gender: userData.gender || '', // Gender alanını ekle
          isActive: userData.isActive !== false, // default true
          createdAt: userData.createdAt,
          lastActiveAt: userData.lastActiveAt,
          photos: userData.photos || [],
          bio: userData.bio || '',
          interests: userData.interests || [],
          relationshipStatus: userData.relationshipStatus || '',
          languages: userData.languages || [],
          // Google ve sosyal medya profil resimleri için ek alanlar
          photoURL: userData.photoURL || null,
          profilePictureUrl: userData.profilePictureUrl || null,
          profilePicUrl: userData.profilePicUrl || null,
          googlePhotoURL: userData.googlePhotoURL || null
          // Premium/subscriber bilgileri kaldırıldı - performans için
        });
      });

      return users;
    } catch (error) {
      console.error('Son kullanıcıları getirme hatası:', error);
      throw error;
    }
  }
};

// Satış servisleri
export const salesService = {
  // Günlük gelir hesapla
  async getDailyRevenue(date?: Date) {
    try {
      const targetDate = date || new Date();
      const startOfDay = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate());
      const endOfDay = new Date(startOfDay);
      endOfDay.setDate(endOfDay.getDate() + 1);
      
      console.log('📊 Günlük gelir hesaplanıyor:', startOfDay.toISOString(), 'to', endOfDay.toISOString());
      
      const salesQuery = query(
        collection(db, 'sales'),
        where('status', '==', 'completed'),
        where('createdAt', '>=', Timestamp.fromDate(startOfDay)),
        where('createdAt', '<', Timestamp.fromDate(endOfDay))
      );
      
      const snapshot = await getDocs(salesQuery);
      let totalRevenue = 0;
      
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        const amount = typeof data.amount === 'number' ? data.amount : 0;
        totalRevenue += amount;
      });
      
      console.log('💰 Günlük gelir:', totalRevenue, 'TRY');
      return totalRevenue;
    } catch (error) {
      console.error('Günlük gelir hesaplama hatası:', error);
      return 0;
    }
  },

  // Aylık gelir hesapla
  async getMonthlyRevenue(month?: Date) {
    try {
      const targetMonth = month || new Date();
      const startOfMonth = new Date(targetMonth.getFullYear(), targetMonth.getMonth(), 1);
      const endOfMonth = new Date(targetMonth.getFullYear(), targetMonth.getMonth() + 1, 1);
      
      console.log('📊 Aylık gelir hesaplanıyor:', startOfMonth.toISOString(), 'to', endOfMonth.toISOString());
      
      const salesQuery = query(
        collection(db, 'sales'),
        where('status', '==', 'completed'),
        where('createdAt', '>=', Timestamp.fromDate(startOfMonth)),
        where('createdAt', '<', Timestamp.fromDate(endOfMonth))
      );
      
      const snapshot = await getDocs(salesQuery);
      let totalRevenue = 0;
      
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        const amount = typeof data.amount === 'number' ? data.amount : 0;
        totalRevenue += amount;
      });
      
      console.log('💰 Aylık gelir:', totalRevenue, 'TRY');
      return totalRevenue;
    } catch (error) {
      console.error('Aylık gelir hesaplama hatası:', error);
      return 0;
    }
  },

  // Kullanıcı email'ini güncelle
  async updateUserEmail(userId: string, email: string) {
    try {
      await updateDoc(doc(db, 'users', userId), {
        email: email
      });
      console.log('✅ Kullanıcı email güncellendi:', email);
      return true;
    } catch (error) {
      console.error('❌ Email güncelleme hatası:', error);
      throw error;
    }
  },

  // Toplam gelir hesapla
  async getTotalRevenue() {
    try {
      console.log('📊 Toplam gelir hesaplanıyor...');
      
      const salesQuery = query(
        collection(db, 'sales'),
        where('status', '==', 'completed')
      );
      
      const snapshot = await getDocs(salesQuery);
      let totalRevenue = 0;
      
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        const amount = typeof data.amount === 'number' ? data.amount : 0;
        totalRevenue += amount;
      });
      
      console.log('💰 Toplam gelir:', totalRevenue, 'TRY');
      return totalRevenue;
    } catch (error) {
      console.error('Toplam gelir hesaplama hatası:', error);
      return 0;
    }
  },

  // Son satışları getir
  async getRecentSales(limitCount: number = 10) {
    try {
      console.log('📊 Son satışlar getiriliyor...');
      
      const salesQuery = query(
        collection(db, 'sales'),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );
      
      const snapshot = await getDocs(salesQuery);
      const sales: SaleRecord[] = [];
      
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        sales.push({
          id: doc.id,
          userId: data.userId || '',
          userName: data.userName || 'Bilinmiyor',
          userEmail: data.userEmail || 'Bilinmiyor',
          productType: data.productType || '',
          productId: data.productId || '',
          amount: typeof data.amount === 'number' ? data.amount : 0,
          currency: data.currency || 'TRY',
          paymentMethod: data.paymentMethod || '',
          status: data.status || 'completed',
          createdAt: data.createdAt,
          metadata: data.metadata || {}
        });
      });
      
      console.log('📊 Son satışlar alındı:', sales.length, 'kayıt');
      return sales;
    } catch (error) {
      console.error('Son satışları getirme hatası:', error);
      return [];
    }
  },

  // Satış istatistikleri getir
  async getSalesStatistics() {
    try {
      console.log('📊 Satış istatistikleri hesaplanıyor...');
      
      const [dailyRevenue, monthlyRevenue, totalRevenue] = await Promise.all([
        this.getDailyRevenue(),
        this.getMonthlyRevenue(),
        this.getTotalRevenue()
      ]);
      
      // Toplam satış sayısı
      const totalSalesSnapshot = await getDocs(
        query(collection(db, 'sales'), where('status', '==', 'completed'))
      );
      
      const stats = {
        dailyRevenue,
        monthlyRevenue,
        totalRevenue,
        totalSalesCount: totalSalesSnapshot.size
      };
      
      console.log('📊 Satış istatistikleri:', stats);
      return stats;
    } catch (error) {
      console.error('Satış istatistikleri getirme hatası:', error);
      return {
        dailyRevenue: 0,
        monthlyRevenue: 0,
        totalRevenue: 0,
        totalSalesCount: 0
      };
    }
  }
};

// Admin kullanıcı yönetimi servisleri
export const adminService = {
  // Tüm admin kullanıcılarını getir
  async getAllAdmins() {
    try {
      const adminQuery = query(
        collection(db, 'admin_users'),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(adminQuery);
      const admins: any[] = [];
      
      snapshot.forEach((doc) => {
        const adminData = doc.data();
        admins.push({
          id: doc.id,
          email: adminData.email || '',
          name: adminData.name || '',
          role: adminData.role || 'admin',
          permissions: adminData.permissions || [],
          isActive: adminData.isActive !== false,
          createdAt: adminData.createdAt,
          createdBy: adminData.createdBy || '',
          lastLoginAt: adminData.lastLoginAt || null
        });
      });
      
      return admins;
    } catch (error) {
      console.error('Admin kullanıcıları getirme hatası:', error);
      throw error;
    }
  },

  // Yeni admin kullanıcı ekle (Firebase Authentication + Firestore)
  async addAdmin(adminData: {
    email: string;
    name: string;
    role: string;
    permissions: string[];
    createdBy: string;
    password?: string; // Şifre opsiyonel - verilmezse otomatik oluşturulur
  }) {
    try {
      // Email'in zaten admin olup olmadığını kontrol et (her iki collection'dan)
      const [existingAdminQuery, existingOldAdminQuery] = await Promise.all([
        getDocs(query(
          collection(db, 'admin_users'),
          where('email', '==', adminData.email)
        )),
        getDocs(query(
          collection(db, 'admins'),
          where('email', '==', adminData.email)
        ))
      ]);
      
      if (!existingAdminQuery.empty || !existingOldAdminQuery.empty) {
        throw new Error('Bu email adresi zaten admin olarak kayıtlı');
      }

      // Otomatik şifre oluştur (eğer verilmediyse)
      const password = adminData.password || this.generateSecurePassword();
      
      console.log('🔐 Yeni admin için oluşturulan şifre:', password);

      // Role göre permissions ayarla
      let rolePermissions = adminData.permissions;
      if (adminData.role === 'super_admin') {
        rolePermissions = ['read', 'write', 'delete', 'user_management', 'system_settings'];
      } else if (adminData.role === 'admin') {
        rolePermissions = ['read', 'write', 'user_management'];
      }
      
      const newAdmin = {
        email: adminData.email,
        name: adminData.name,
        role: adminData.role,
        permissions: rolePermissions,
        createdBy: adminData.createdBy,
        isActive: true,
        createdAt: Timestamp.now(),
        lastLoginAt: null,
        temporaryPassword: password,
        mustChangePassword: true
      };
      
      // 1. admin_users collection'ına ekle (yeni sistem)
      const docRef = await addDoc(collection(db, 'admin_users'), newAdmin);
      
      // 2. admins collection'ına da ekle (eski sistem uyumluluğu için)
      const oldSystemAdmin = {
        id: docRef.id,
        email: adminData.email,
        name: adminData.name,
        role: adminData.role,
        permissions: rolePermissions,
        isActive: true,
        createdAt: Timestamp.now(),
        createdBy: adminData.createdBy,
        temporaryPassword: password
      };
      
      // Belirli bir ID ile admins collection'ına ekle
      await setDoc(doc(db, 'admins', docRef.id), oldSystemAdmin);
      
      console.log('✅ Admin hem yeni hem eski sisteme kaydedildi');
      console.log('📋 Role göre permissions:', rolePermissions);
      
      return {
        id: docRef.id,
        ...newAdmin,
        password: password
      };
    } catch (error) {
      console.error('Admin ekleme hatası:', error);
      throw error;
    }
  },

  // Güvenli şifre oluşturma
  generateSecurePassword(length: number = 12): string {
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return password;
  },

  // Admin giriş kontrolü (email + şifre)
  async loginAdmin(email: string, password: string) {
    try {
      const adminQuery = query(
        collection(db, 'admin_users'),
        where('email', '==', email),
        where('isActive', '==', true)
      );
      
      const snapshot = await getDocs(adminQuery);
      
      if (snapshot.empty) {
        throw new Error('Admin kullanıcı bulunamadı veya hesap pasif');
      }
      
      const adminDoc = snapshot.docs[0];
      const adminData = adminDoc.data();
      
      // Geçici şifre kontrolü
      if (adminData.temporaryPassword === password) {
        // Son giriş zamanını güncelle
        await this.updateLastLogin(adminDoc.id);
        
        return {
          id: adminDoc.id,
          email: adminData.email,
          name: adminData.name,
          role: adminData.role,
          permissions: adminData.permissions || [],
          mustChangePassword: adminData.mustChangePassword || false
        };
      } else {
        throw new Error('Şifre hatalı');
      }
    } catch (error) {
      console.error('Admin giriş hatası:', error);
      throw error;
    }
  },

  // Admin şifre değiştirme
  async changeAdminPassword(adminId: string, newPassword: string) {
    try {
      const adminRef = doc(db, 'admin_users', adminId);
      await updateDoc(adminRef, {
        temporaryPassword: newPassword,
        mustChangePassword: false,
        passwordChangedAt: Timestamp.now()
      });
      
      console.log('✅ Admin şifresi güncellendi');
      return true;
    } catch (error) {
      console.error('Şifre değiştirme hatası:', error);
      throw error;
    }
  },

  // Admin kullanıcı güncelle
  async updateAdmin(adminId: string, updateData: {
    name?: string;
    role?: string;
    permissions?: string[];
    isActive?: boolean;
  }) {
    try {
      const adminRef = doc(db, 'admin_users', adminId);
      await updateDoc(adminRef, {
        ...updateData,
        updatedAt: Timestamp.now()
      });
      
      return true;
    } catch (error) {
      console.error('Admin güncelleme hatası:', error);
      throw error;
    }
  },

  // Admin kullanıcı sil
  async deleteAdmin(adminId: string) {
    try {
      const adminRef = doc(db, 'admin_users', adminId);
      await deleteDoc(adminRef);
      
      return true;
    } catch (error) {
      console.error('Admin silme hatası:', error);
      throw error;
    }
  },

  // Admin kullanıcının varlığını kontrol et
  async checkAdminByEmail(email: string) {
    try {
      const adminQuery = query(
        collection(db, 'admin_users'),
        where('email', '==', email),
        where('isActive', '==', true)
      );
      
      const snapshot = await getDocs(adminQuery);
      
      if (!snapshot.empty) {
        const adminDoc = snapshot.docs[0];
        const adminData = adminDoc.data();
        
        return {
          id: adminDoc.id,
          email: adminData.email,
          name: adminData.name,
          role: adminData.role,
          permissions: adminData.permissions || []
        };
      }
      
      return null;
    } catch (error) {
      console.error('Admin kontrol hatası:', error);
      return null;
    }
  },

  // Admin giriş zamanını güncelle
  async updateLastLogin(adminId: string) {
    try {
      const adminRef = doc(db, 'admin_users', adminId);
      await updateDoc(adminRef, {
        lastLoginAt: Timestamp.now()
      });
      
      return true;
    } catch (error) {
      console.error('Son giriş güncelleme hatası:', error);
      return false;
    }
  }
};

// Geliştirici araçları servisleri
export const developerToolsService = {
  // Beni beğen - admin kullanıcısını diğer kullanıcılara beğendirme
  async likeMeFromRandomUsers(adminUserId: string, count: number = 10) {
    try {
      console.log('🔄 Rastgele kullanıcıların admin kullanıcıyı beğenmesi işlemi başlatılıyor...');
      
      // Rastgele kullanıcıları al
      const usersSnapshot = await getDocs(query(
        collection(db, 'users'),
        limit(count * 2) // Fazladan al ki yeterli olsun
      ));
      
      const randomUsers = usersSnapshot.docs
        .slice(0, count)
        .map(doc => ({ id: doc.id, ...doc.data() }));
      
      let processedCount = 0;
      for (const user of randomUsers) {
        try {
          // Likes collection'ına ekle
          await addDoc(collection(db, 'likes'), {
            userId: user.id,
            likedUserId: adminUserId,
            createdAt: Timestamp.now(),
            type: 'like'
          });
          processedCount++;
        } catch (error) {
          console.error(`Kullanıcı ${user.id} için like eklenemedi:`, error);
        }
      }
      
      console.log(`✅ ${processedCount} kullanıcı admin kullanıcıyı beğendi`);
      return processedCount;
    } catch (error) {
      console.error('Beni beğen işlemi hatası:', error);
      throw error;
    }
  },

  // İstanbul civarına konum atama
  async assignIstanbulLocations() {
    try {
      console.log('🔄 İstanbul civarına konum atama işlemi başlatılıyor...');
      
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const totalUsers = usersSnapshot.docs.length;
      let processedCount = 0;
      
      console.log(`📊 Toplam ${totalUsers} kullanıcı bulundu, konum güncelleme başlıyor...`);
      
      // Batch işleme için kullanıcıları 10'lu gruplara ayır
      const batchSize = 10;
      const userBatches = [];
      
      for (let i = 0; i < usersSnapshot.docs.length; i += batchSize) {
        userBatches.push(usersSnapshot.docs.slice(i, i + batchSize));
      }
      
      console.log(`🔧 ${userBatches.length} batch'te işlenecek (her batch'te ${batchSize} kullanıcı)`);
      
      const updateUserLocation = async (userDoc: any) => {
        try {
          // İstanbul koordinatları civarında rastgele konum
          const lat = 40.9 + (Math.random() * 0.2); // 40.9 - 41.1
          const lng = 28.8 + (Math.random() * 0.2); // 28.8 - 29.0

          await updateDoc(userDoc.ref, {
            location: {
              latitude: lat,
              longitude: lng,
              city: 'İstanbul',
              country: 'Türkiye'
            },
            updatedAt: Timestamp.now()
          });
          processedCount++;
        } catch (error) {
          console.error(`Kullanıcı ${userDoc.id} konumu güncellenemedi:`, error);
        }
      };

      for (let batchIndex = 0; batchIndex < userBatches.length; batchIndex++) {
        const batch = userBatches[batchIndex];
        const promises = batch.map(updateUserLocation);
        
        // Bu batch'i işle
        await Promise.all(promises);
        
        // İlerleme mesajı
        const progress = Math.round((processedCount / totalUsers) * 100);
        console.log(`📍 Batch ${batchIndex + 1}/${userBatches.length} tamamlandı - İlerleme: %${progress} (${processedCount}/${totalUsers})`);
        
        // Rate limiting için kısa bir bekleme
        if (batchIndex < userBatches.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
      
      console.log(`✅ ${processedCount} kullanıcının konumu İstanbul civarına atandı`);
      return processedCount;
    } catch (error) {
      console.error('Konum atama hatası:', error);
      throw error;
    }
  },

  // Tüm beğenileri ve mesajları sil
  async deleteAllInteractions() {
    try {
      console.log('🔄 Tüm beğeniler ve mesajlar siliniyor...');
      
      // Likes silme
      const likesSnapshot = await getDocs(collection(db, 'likes'));
      const likesPromises = likesSnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(likesPromises);
      console.log(`🗑️ ${likesSnapshot.size} like silindi`);
      
      // Matches silme
      const matchesSnapshot = await getDocs(collection(db, 'matches'));
      const matchesPromises = matchesSnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(matchesPromises);
      console.log(`🗑️ ${matchesSnapshot.size} match silindi`);
      
      // Messages silme
      const messagesSnapshot = await getDocs(collection(db, 'messages'));
      const messagesPromises = messagesSnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(messagesPromises);
      console.log(`🗑️ ${messagesSnapshot.size} mesaj silindi`);
      
      console.log('✅ Tüm beğeniler ve mesajlar silindi');
      return {
        likes: likesSnapshot.size,
        matches: matchesSnapshot.size,
        messages: messagesSnapshot.size
      };
    } catch (error) {
      console.error('Etkileşim silme hatası:', error);
      throw error;
    }
  },

  // 🔄 HYBRID: Rastgele gönderiler oluştur (Supabase Primary + Firebase Fallback)
  async createRandomPosts(count: number = 5) {
    try {
      console.log(`🔄 HYBRID: ${count} rastgele gönderi oluşturuluyor...`);
      
      // Supabase service'ini import et
      const { supabaseService } = await import('./supabaseService');
      
      const postTemplates = [
        { text: 'Bugün harika bir gün! 🌟', type: 'text' },
        { text: 'Kahve içmeye kim var? ☕', type: 'text' },
        { text: 'Yeni bir gün, yeni umutlar 🌅', type: 'text' },
        { text: 'Müzik ruha şifa 🎵', type: 'text' },
        { text: 'Kitap okumayı seviyorum 📚', type: 'text' },
        { text: 'Spor yapmak çok güzel 💪', type: 'text' },
        { text: 'Doğa yürüyüşü keyifli 🌳', type: 'text' },
        { text: 'Film izlemeyi seviyorum 🎬', type: 'text' },
        { text: 'Güzel bir manzara 🏞️', type: 'image' },
        { text: 'Lezzetli yemek! 🍽️', type: 'image' },
        { text: 'Arkadaşlarla güzel anlar 👫', type: 'image' },
        { text: 'Hobilerimi paylaşıyorum 🎨', type: 'image' },
        { text: 'Tatil anıları 🏖️', type: 'image' },
        { text: 'Şehir gezisi 🏙️', type: 'image' }
      ];
      
      // Rastgele test resimleri (Unsplash'den ücretsiz resimler)
      const randomImages = [
        'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=400&fit=crop',
        'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=400&h=400&fit=crop',
        'https://images.unsplash.com/photo-1501594907352-04cda38ebc29?w=400&h=400&fit=crop',
        'https://images.unsplash.com/photo-1518837695005-2083093ee35b?w=400&h=400&fit=crop',
        'https://images.unsplash.com/photo-1527004013197-933c4bb611b3?w=400&h=400&fit=crop',
        'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=400&h=400&fit=crop',
        'https://images.unsplash.com/photo-1540979388789-6cee28a1cdc9?w=400&h=400&fit=crop',
        'https://images.unsplash.com/photo-1500375592092-40eb2168fd21?w=400&h=400&fit=crop',
        'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=400&h=400&fit=crop',
        'https://images.unsplash.com/photo-1534224039826-c7a0eda0e6b3?w=400&h=400&fit=crop',
        'https://images.unsplash.com/photo-1551334787-21e6bd3ab135?w=400&h=400&fit=crop',
        'https://images.unsplash.com/photo-1551963831-b3b1ca40c98e?w=400&h=400&fit=crop'
      ];
      
      // 🔄 HYBRID: Önce Supabase'den kullanıcıları dene
      let users: any[] = [];
      let useSupabase = true;
      
      try {
        console.log('🔄 HYBRID: Supabase\'den kullanıcılar alınıyor...');
        users = await supabaseService.getAllUsers({ limit: 20 });
        
        if (users.length === 0) {
          throw new Error('Supabase\'de kullanıcı bulunamadı');
        }
        
        console.log(`✅ HYBRID: Supabase'den ${users.length} kullanıcı alındı`);
      } catch (supabaseError) {
        console.log('⚠️ HYBRID: Supabase kullanıcı hatası, Firebase fallback...', supabaseError);
        useSupabase = false;
        
        // Firebase fallback
        const usersSnapshot = await getDocs(query(collection(db, 'users'), limit(20)));
        users = usersSnapshot.docs.map(doc => ({ 
          id: doc.id, 
          name: doc.data().name,
          profilePicUrl: doc.data().profilePicUrl,
          ...doc.data() 
        }));
        
        console.log(`✅ HYBRID: Firebase fallback'ten ${users.length} kullanıcı alındı`);
      }
      
      if (users.length === 0) {
        throw new Error('Hiçbir kaynakta kullanıcı bulunamadı');
      }
      
      let createdCount = 0;
      
      // Gönderiler oluştur: ilk 3'ü resimli, son 2'si sadece yazı
      for (let i = 0; i < count; i++) {
        const randomUser = users[Math.floor(Math.random() * users.length)];
        const shouldHaveImage = i < 3; // İlk 3 gönderi resimli olacak
        
        // Resimli veya yazı gönderisi seç
        const availablePosts = postTemplates.filter(post => 
          shouldHaveImage ? post.type === 'image' : post.type === 'text'
        );
        
        const randomPost = availablePosts[Math.floor(Math.random() * availablePosts.length)];
        
        try {
          // Resimli gönderi için rastgele resim seç
          let imageUrls: string[] | undefined = undefined;
          if (shouldHaveImage) {
            const randomImage = randomImages[Math.floor(Math.random() * randomImages.length)];
            imageUrls = [randomImage];
          }
          
          // 🆕 Gönderi oluşturma zamanı ve 24 saat sonrası
          const createdAt = new Date();
          const postId = `post_${randomUser.id}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          
          let postSuccess = false;
          let method = '';
          
          // 🔄 HYBRID: Önce Supabase'e dene
          if (useSupabase) {
            try {
              console.log(`🔄 HYBRID: Post ${i + 1} Supabase'e oluşturuluyor...`);
              
              const supabaseSuccess = await supabaseService.createPost({
                id: postId,
                userId: randomUser.id,
                userName: randomUser.name || 'Anonim',
                userProfilePicUrl: randomUser.profilePicUrl || null,
                content: randomPost.text,
                imageUrls: imageUrls,
                createdAt: createdAt,
              });
              
              if (supabaseSuccess) {
                postSuccess = true;
                method = 'Supabase';
                console.log(`✅ HYBRID: Post ${i + 1} Supabase'e oluşturuldu`);
              } else {
                throw new Error('Supabase createPost false döndü');
              }
            } catch (supabasePostError) {
              console.log(`⚠️ HYBRID: Post ${i + 1} Supabase hatası, Firebase fallback...`, supabasePostError);
              
              // Firebase fallback
              const expiresAt = Timestamp.fromDate(new Date(Date.now() + 24 * 60 * 60 * 1000));
              
              await addDoc(collection(db, 'posts'), {
                userId: randomUser.id,
                userName: randomUser.name || 'Anonim',
                userProfilePicUrl: randomUser.profilePicUrl || null,
                content: randomPost.text,
                imageUrls: imageUrls,
                createdAt: Timestamp.fromDate(createdAt),
                expiresAt: expiresAt,
                likeCount: Math.floor(Math.random() * 15),
                commentCount: Math.floor(Math.random() * 8),
                likedByUserIds: [],
                isDeleted: false
              });
              
              postSuccess = true;
              method = 'Firebase (fallback)';
              console.log(`✅ HYBRID: Post ${i + 1} Firebase fallback'e oluşturuldu`);
            }
          } else {
            // Direkt Firebase kullan
            const expiresAt = Timestamp.fromDate(new Date(Date.now() + 24 * 60 * 60 * 1000));
            
            await addDoc(collection(db, 'posts'), {
              userId: randomUser.id,
              userName: randomUser.name || 'Anonim',
              userProfilePicUrl: randomUser.profilePicUrl || null,
              content: randomPost.text,
              imageUrls: imageUrls,
              createdAt: Timestamp.fromDate(createdAt),
              expiresAt: expiresAt,
              likeCount: Math.floor(Math.random() * 15),
              commentCount: Math.floor(Math.random() * 8),
              likedByUserIds: [],
              isDeleted: false
            });
            
            postSuccess = true;
            method = 'Firebase (primary)';
            console.log(`✅ HYBRID: Post ${i + 1} Firebase'e oluşturuldu`);
          }
          
          if (postSuccess) {
            createdCount++;
            console.log(`📝 HYBRID: Gönderi ${i + 1}/${count} oluşturuldu - ${shouldHaveImage ? 'Resimli' : 'Sadece yazı'} (${method})`);
          }
        } catch (error) {
          console.error(`❌ HYBRID: Gönderi ${i + 1} oluşturulamadı:`, error);
        }
      }
      
      console.log(`✅ HYBRID: ${createdCount} gönderi oluşturuldu (${Math.min(3, count)} resimli + ${Math.max(0, count - 3)} yazı) - Hepsi 24 saat sonra otomatik silinecek`);
      return createdCount;
    } catch (error) {
      console.error('❌ HYBRID: Gönderi oluşturma hatası:', error);
      throw error;
    }
  },

  // 🔄 HYBRID: Tüm gönderileri sil (Supabase Primary + Firebase Fallback)
  async deleteAllPosts() {
    try {
      console.log('🔄 HYBRID: Tüm gönderiler siliniyor...');
      
      // Supabase service'ini import et
      const { supabaseService } = await import('./supabaseService');
      
      let totalDeleted = 0;
      let method = '';
      
      // 🔄 HYBRID: Önce Supabase'den silmeyi dene
      try {
        console.log('🔄 HYBRID: Supabase\'den gönderiler siliniyor...');
        
        const supabaseDeleted = await supabaseService.deleteAllPosts();
        
        if (supabaseDeleted > 0) {
          totalDeleted += supabaseDeleted;
          method = 'Supabase';
          console.log(`✅ HYBRID: Supabase'den ${supabaseDeleted} gönderi silindi`);
        }
      } catch (supabaseError) {
        console.log('⚠️ HYBRID: Supabase silme hatası, Firebase fallback...', supabaseError);
      }
      
      // Firebase'den de sil (hybrid sistem için her ikisini de temizle)
      try {
        console.log('🔄 HYBRID: Firebase\'den gönderiler siliniyor...');
        
        const postsSnapshot = await getDocs(collection(db, 'posts'));
        const deletePromises = postsSnapshot.docs.map(doc => deleteDoc(doc.ref));
        await Promise.all(deletePromises);
        
        if (postsSnapshot.size > 0) {
          totalDeleted += postsSnapshot.size;
          method = method ? `${method} + Firebase` : 'Firebase';
          console.log(`✅ HYBRID: Firebase'den ${postsSnapshot.size} gönderi silindi`);
        }
      } catch (firebaseError) {
        console.error('❌ HYBRID: Firebase silme hatası:', firebaseError);
      }
      
      console.log(`✅ HYBRID: Toplam ${totalDeleted} gönderi silindi (${method})`);
      return totalDeleted;
    } catch (error) {
      console.error('❌ HYBRID: Gönderi silme hatası:', error);
      throw error;
    }
  },

  // 50 Türk kadın kullanıcı oluştur
  async create50TurkishWomen() {
    try {
      console.log('🔄 50 Türk kadın kullanıcı oluşturuluyor...');
      
      const turkishFemaleNames = [
        'Ayşe', 'Fatma', 'Emine', 'Hatice', 'Zeynep', 'Elif', 'Merve', 'Nur', 'Büşra', 'Seda',
        'Özge', 'Esra', 'Gizem', 'Cansu', 'Burcu', 'Deniz', 'İrem', 'Ebru', 'Pınar', 'Sevgi',
        'Yeliz', 'Begüm', 'Ece', 'Tuğba', 'Seher', 'Didem', 'Nihan', 'Cemre', 'Yasemin', 'Derya',
        'Sinem', 'Özlem', 'Berna', 'Melisa', 'Neslihan', 'Gülşen', 'Sibel', 'Melek', 'Zehra', 'Seval',
        'Betül', 'Aslı', 'Çiğdem', 'Funda', 'Handan', 'İlknur', 'Jülide', 'Kezban', 'Leyla', 'Müge'
      ];
      
      const surnames = [
        'Yılmaz', 'Kaya', 'Demir', 'Şahin', 'Çelik', 'Aydın', 'Özkan', 'Arslan', 'Doğan', 'Kılıç',
        'Aslan', 'Çetin', 'Kara', 'Koç', 'Kurt', 'Özdemir', 'Şen', 'Türk', 'Uçar', 'Yıldız'
      ];
      
      const interests = [
        'Müzik', 'Kitap Okuma', 'Sinema', 'Seyahat', 'Yoga', 'Fotoğrafçılık', 'Yemek Yapma',
        'Dans', 'Resim', 'Spor', 'Doğa Yürüyüşü', 'Kahve', 'Sanat', 'Teknoloji', 'Moda'
      ];
      
      let createdCount = 0;
      
      for (let i = 0; i < 50; i++) {
        try {
          const name = turkishFemaleNames[Math.floor(Math.random() * turkishFemaleNames.length)];
          const surname = surnames[Math.floor(Math.random() * surnames.length)];
          const age = Math.floor(Math.random() * 25) + 18; // 18-42 yaş
          
          // Rastgele ilgi alanları seç (2-5 adet)
          const userInterests: string[] = [];
          const interestCount = Math.floor(Math.random() * 4) + 2;
          for (let j = 0; j < interestCount; j++) {
            const interest = interests[Math.floor(Math.random() * interests.length)];
            if (!userInterests.includes(interest)) {
              userInterests.push(interest);
            }
          }
          
          // İstanbul koordinatları
          const lat = 40.9 + (Math.random() * 0.2);
          const lng = 28.8 + (Math.random() * 0.2);
          
          // ✅ Doğum tarihi hesapla (Flutter uyumlu)
          const birthDate = new Date();
          birthDate.setFullYear(birthDate.getFullYear() - age);
          
          // 🆕 İlgilendiği cinsiyet dağılımı (kadın kullanıcılar için - Flutter uyumlu)
          let interestedIn: string[];
          if (Math.random() < 0.8) {
            // %80 erkeklerle ilgilenir (heteroseksüel)
            interestedIn = ['Erkek'];
          } else {
            // %20 kadınlarla ilgilenir (lezbiyen)
            interestedIn = ['Kadın'];
          }
          
          const userData = {
            name: `${name} ${surname}`,
            email: `test${i + 2001}.${name.toLowerCase()}.${surname.toLowerCase()}@gmail.com`,
            age: age,
            birthDate: birthDate, // ✅ Flutter uyumlu doğum tarihi
            gender: 'Kadın', // ✅ DÜZELT: Flutter uyumlu format
            interestedIn: interestedIn, // ✅ DÜZELT: Flutter uyumlu array format
            bio: `Merhaba! Ben ${name}. ${age} yaşındayım ve İstanbul'da yaşıyorum.`,
            interests: userInterests,
            relationshipStatus: 'single',
            languages: ['Türkçe'],
            photos: [],
            profilePicUrl: null, // Profil fotoğrafı yok
            location: {
              latitude: lat,
              longitude: lng,
              city: 'İstanbul',
              country: 'Türkiye'
            },
            isActive: true,
            createdAt: new Date(), // ✅ Flutter uyumlu format
            lastActive: new Date(), // ✅ Flutter uyumlu format
            totalTokens: 0,
            subscriberCount: 0
          };
          
          await addDoc(collection(db, 'users'), userData);
          createdCount++;
          
          if (i % 10 === 0) {
            console.log(`📊 ${i}/50 kullanıcı oluşturuldu...`);
          }
        } catch (error) {
          console.error(`Kullanıcı ${i + 1} oluşturulamadı:`, error);
        }
      }
      
      console.log(`✅ ${createdCount} Türk kadın kullanıcı oluşturuldu`);
      return createdCount;
    } catch (error) {
      console.error('Türk kadın kullanıcı oluşturma hatası:', error);
      throw error;
    }
  },

  // Tüm kullanıcıları sil (TEHLİKELİ)
  async deleteAllUsers() {
    try {
      console.log('🔄 TÜM KULLANICILAR SİLİNİYOR...');
      
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const deletePromises = usersSnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
      
      console.log(`✅ ${usersSnapshot.size} kullanıcı silindi`);
      return usersSnapshot.size;
    } catch (error) {
      console.error('Kullanıcı silme hatası:', error);
      throw error;
    }
  },

  // Mevcut posts'ları Flutter formatına güncelleme
  async updatePostsToFlutterFormat() {
    try {
      console.log('🔄 Mevcut posts Flutter formatına güncelleniyor...');
      
      const postsSnapshot = await getDocs(collection(db, 'posts'));
      let updatedCount = 0;
      
      for (const postDoc of postsSnapshot.docs) {
        try {
          const postData = postDoc.data();
          
          // Eksik alanları kontrol et ve ekle
          const updateData: any = {};
          
          if (postData.isDeleted === undefined) {
            updateData.isDeleted = false;
          }
          
          if (postData.likeCount === undefined && postData.likes !== undefined) {
            updateData.likeCount = postData.likes || 0;
          } else if (postData.likeCount === undefined) {
            updateData.likeCount = 0;
          }
          
          if (postData.commentCount === undefined && postData.comments !== undefined) {
            updateData.commentCount = postData.comments || 0;
          } else if (postData.commentCount === undefined) {
            updateData.commentCount = 0;
          }
          
          if (postData.likedByUserIds === undefined) {
            updateData.likedByUserIds = [];
          }
          
          if (postData.userProfilePicUrl === undefined) {
            updateData.userProfilePicUrl = null;
          }
          
          if (postData.imageUrls === undefined) {
            updateData.imageUrls = null;
          }
          
          // 🆕 24 saatlik otomatik silme için expiresAt alanı ekle
          if (postData.expiresAt === undefined && postData.createdAt) {
            const createdAt = postData.createdAt.toDate ? postData.createdAt.toDate() : new Date(postData.createdAt);
            const expiresAt = new Date(createdAt.getTime() + (24 * 60 * 60 * 1000)); // 24 saat sonra
            updateData.expiresAt = expiresAt;
          }
          
          // Güncellenecek alan varsa güncelle
          if (Object.keys(updateData).length > 0) {
            await updateDoc(postDoc.ref, updateData);
            updatedCount++;
          }
        } catch (error) {
          console.error(`Post ${postDoc.id} güncellenemedi:`, error);
        }
      }
      
      console.log(`✅ ${updatedCount} post Flutter formatına güncellendi`);
      return updatedCount;
    } catch (error) {
      console.error('Posts güncelleme hatası:', error);
      throw error;
    }
  },

  // 🆕 Süresi dolan gönderileri temizleme
  async cleanExpiredPosts() {
    try {
      console.log('🔄 Süresi dolan gönderiler temizleniyor...');
      
      const now = new Date();
      const expiredPostsQuery = query(
        collection(db, 'posts'),
        where('isDeleted', '==', false),
        where('expiresAt', '<=', now),
        limit(100) // Aynı anda en fazla 100 gönderi temizle
      );
      
      const expiredPostsSnapshot = await getDocs(expiredPostsQuery);
      let deletedCount = 0;
      
      for (const postDoc of expiredPostsSnapshot.docs) {
        try {
          await updateDoc(postDoc.ref, { isDeleted: true });
          deletedCount++;
        } catch (error) {
          console.error(`Post ${postDoc.id} silinemedi:`, error);
        }
      }
      
      console.log(`✅ ${deletedCount} süresi dolan gönderi temizlendi`);
      return {
        success: true,
        deletedCount,
        message: `${deletedCount} süresi dolan gönderi başarıyla temizlendi!`
      };
    } catch (error) {
      console.error('Süresi dolan gönderi temizleme hatası:', error);
      return {
        success: false,
        message: `Temizleme sırasında hata oluştu: ${error}`
      };
    }
  },

  // 🆕 Tüm mevcut gönderilere expiresAt alanı ekleme
  async addExpiresAtToAllPosts() {
    try {
      console.log('🔄 Tüm mevcut gönderilere expiresAt alanı ekleniyor...');
      
      const postsSnapshot = await getDocs(collection(db, 'posts'));
      let updatedCount = 0;
      
      for (const postDoc of postsSnapshot.docs) {
        try {
          const postData = postDoc.data();
          
          // expiresAt alanı yoksa ekle
          if (!postData.expiresAt && postData.createdAt) {
            const createdAt = postData.createdAt.toDate ? postData.createdAt.toDate() : new Date(postData.createdAt);
            const expiresAt = new Date(createdAt.getTime() + (24 * 60 * 60 * 1000)); // 24 saat sonra
            
            await updateDoc(postDoc.ref, { expiresAt });
            updatedCount++;
            
            if (updatedCount % 10 === 0) {
              console.log(`📊 ${updatedCount} gönderi güncellendi...`);
            }
          }
        } catch (error) {
          console.error(`Post ${postDoc.id} güncellenemedi:`, error);
        }
      }
      
      console.log(`✅ ${updatedCount} gönderiye expiresAt alanı eklendi`);
      return {
        success: true,
        updatedCount,
        message: `${updatedCount} gönderiye başarıyla expiresAt alanı eklendi!`
      };
    } catch (error) {
      console.error('ExpiresAt ekleme hatası:', error);
      return {
        success: false,
        message: `ExpiresAt ekleme sırasında hata oluştu: ${error}`
      };
    }
  },



  // Manual like ekleme - email adresleri ile
  async addManualLike(fromUserEmail: string, toUserEmail: string) {
    try {
      console.log(`🔄 Manual like ekleniyor: ${fromUserEmail} -> ${toUserEmail}`);
      
      // From user'ı email ile bul
      const fromUserQuery = query(
        collection(db, 'users'),
        where('email', '==', fromUserEmail),
        limit(1)
      );
      const fromUserSnapshot = await getDocs(fromUserQuery);
      
      if (fromUserSnapshot.empty) {
        throw new Error(`Gönderen kullanıcı bulunamadı: ${fromUserEmail}`);
      }
      
      // To user'ı email ile bul
      const toUserQuery = query(
        collection(db, 'users'),
        where('email', '==', toUserEmail),
        limit(1)
      );
      const toUserSnapshot = await getDocs(toUserQuery);
      
      if (toUserSnapshot.empty) {
        throw new Error(`Hedef kullanıcı bulunamadı: ${toUserEmail}`);
      }
      
      const fromUserId = fromUserSnapshot.docs[0].id;
      const toUserId = toUserSnapshot.docs[0].id;
      const fromUserData = fromUserSnapshot.docs[0].data();
      const toUserData = toUserSnapshot.docs[0].data();
      
      // ✅ DÜZELT: Flutter uyumlu field isimleri ile like kontrolü
      const existingLikeQuery = query(
        collection(db, 'likes'),
        where('senderId', '==', fromUserId), // userId -> senderId
        where('receiverId', '==', toUserId)  // likedUserId -> receiverId
      );
      const existingLikeSnapshot = await getDocs(existingLikeQuery);
      
      if (!existingLikeSnapshot.empty) {
        console.log(`⚠️ ${fromUserData.name} zaten ${toUserData.name} kullanıcısını beğenmiş`);
        
        // Karşılıklı beğeni var mı kontrol et
        const reverseLikeQuery = query(
          collection(db, 'likes'),
          where('senderId', '==', toUserId),
          where('receiverId', '==', fromUserId)
        );
        const reverseLikeSnapshot = await getDocs(reverseLikeQuery);
        
        if (!reverseLikeSnapshot.empty) {
          // Karşılıklı beğeni var, match var mı kontrol et
          const existingMatchQuery = query(
            collection(db, 'matches'),
            where('users', 'array-contains', fromUserId)
          );
          const existingMatchSnapshot = await getDocs(existingMatchQuery);
          
          let matchExists = false;
          existingMatchSnapshot.docs.forEach(doc => {
            const matchData = doc.data();
            const users = matchData.users || [];
            if (users.includes(toUserId)) {
              matchExists = true;
            }
          });
          
          if (!matchExists) {
            // Match oluştur
            console.log(`💕 Karşılıklı beğeni mevcut, match oluşturuluyor: ${fromUserData.name} ↔ ${toUserData.name}`);
            await addDoc(collection(db, 'matches'), {
              users: [fromUserId, toUserId],
              createdAt: Timestamp.now(),
              lastMessageAt: null,
              isActive: true,
              addedByAdmin: true,
              adminNote: 'Admin tarafından manuel match oluşturuldu'
            });
            
            return {
              success: true,
              message: `🎉 MATCH! ${fromUserData.name} ve ${toUserData.name} eşleşti!`,
              isMatch: true,
              fromUser: { id: fromUserId, name: fromUserData.name, email: fromUserEmail },
              toUser: { id: toUserId, name: toUserData.name, email: toUserEmail }
            };
          } else {
            return {
              success: true,
              message: `ℹ️ ${fromUserData.name} zaten ${toUserData.name} kullanıcısını beğenmiş ve match mevcut`,
              isMatch: true,
              fromUser: { id: fromUserId, name: fromUserData.name, email: fromUserEmail },
              toUser: { id: toUserId, name: toUserData.name, email: toUserEmail }
            };
          }
        } else {
          return {
            success: true,
            message: `ℹ️ ${fromUserData.name} zaten ${toUserData.name} kullanıcısını beğenmiş (tek yönlü beğeni)`,
            isMatch: false,
            fromUser: { id: fromUserId, name: fromUserData.name, email: fromUserEmail },
            toUser: { id: toUserId, name: toUserData.name, email: toUserEmail }
          };
        }
      }
      
      // ✅ DÜZELT: Flutter uyumlu field isimleri ile like ekleme
      await addDoc(collection(db, 'likes'), {
        senderId: fromUserId,      // userId -> senderId (Flutter uyumlu)
        receiverId: toUserId,      // likedUserId -> receiverId (Flutter uyumlu)
        timestamp: Date.now(),     // Flutter uyumlu timestamp
        createdAt: Timestamp.now(), // Timestamp da ekle
        type: 'like',
        fromUserEmail: fromUserEmail,
        toUserEmail: toUserEmail,
        addedByAdmin: true,
        adminNote: 'Admin tarafından manuel eklendi'
      });
      
      console.log(`✅ Manual like başarıyla eklendi: ${fromUserData.name} (${fromUserEmail}) -> ${toUserData.name} (${toUserEmail})`);
      
      // 🔍 Karşılıklı beğeni var mı kontrol et (match için)
      const reverseLikeQuery = query(
        collection(db, 'likes'),
        where('senderId', '==', toUserId),
        where('receiverId', '==', fromUserId)
      );
      const reverseLikeSnapshot = await getDocs(reverseLikeQuery);
      
      if (!reverseLikeSnapshot.empty) {
        // 💕 Karşılıklı beğeni var! Match oluştur
        console.log(`💕 Karşılıklı beğeni tespit edildi! Match oluşturuluyor: ${fromUserData.name} ↔ ${toUserData.name}`);
        
        // Önce mevcut match var mı kontrol et
        const existingMatchQuery = query(
          collection(db, 'matches'),
          where('users', 'array-contains', fromUserId)
        );
        const existingMatchSnapshot = await getDocs(existingMatchQuery);
        
        let matchExists = false;
        existingMatchSnapshot.docs.forEach(doc => {
          const matchData = doc.data();
          const users = matchData.users || [];
          if (users.includes(toUserId)) {
            matchExists = true;
          }
        });
        
        if (!matchExists) {
          await addDoc(collection(db, 'matches'), {
            users: [fromUserId, toUserId],
            createdAt: Timestamp.now(),
            lastMessageAt: null,
            isActive: true,
            addedByAdmin: true,
            adminNote: 'Admin manual like sonrası otomatik match'
          });
          
          return {
            success: true,
            message: `🎉 MATCH! ${fromUserData.name} kullanıcısı ${toUserData.name} kullanıcısını beğendi ve karşılıklı beğeni sonucu eşleştiler!`,
            isMatch: true,
            fromUser: { id: fromUserId, name: fromUserData.name, email: fromUserEmail },
            toUser: { id: toUserId, name: toUserData.name, email: toUserEmail }
          };
        }
      }
      
      return {
        success: true,
        message: `✅ ${fromUserData.name} kullanıcısı ${toUserData.name} kullanıcısını beğendi! (Tek yönlü beğeni)`,
        isMatch: false,
        fromUser: { id: fromUserId, name: fromUserData.name, email: fromUserEmail },
        toUser: { id: toUserId, name: toUserData.name, email: toUserEmail }
      };
    } catch (error) {
      console.error('Manual like ekleme hatası:', error);
      throw error;
    }
  },

  // 🔧 Mevcut likes collection'ını Flutter uyumlu hale getir
  async fixExistingLikesFormat() {
    try {
      console.log('🔄 Mevcut likes Flutter uyumlu format kontrolü başlatılıyor...');
      
      // Tüm likes'ları getir
      const likesSnapshot = await getDocs(collection(db, 'likes'));
      console.log(`📊 Toplam ${likesSnapshot.size} beğeni kaydı bulundu`);
      
      let needsUpdateCount = 0;
      let updatedCount = 0;
      let errorCount = 0;
      
      for (const likeDoc of likesSnapshot.docs) {
        try {
          const likeData = likeDoc.data();
          const updateData: any = {};
          let needsUpdate = false;
          
          // Eski format kontrolü ve düzeltme
          if (likeData.userId && !likeData.senderId) {
            updateData.senderId = likeData.userId;
            needsUpdate = true;
          }
          
          if (likeData.likedUserId && !likeData.receiverId) {
            updateData.receiverId = likeData.likedUserId;
            needsUpdate = true;
          }
          
          // Timestamp kontrolü
          if (!likeData.timestamp && likeData.createdAt) {
            const createdAt = likeData.createdAt;
            if (createdAt.toDate) {
              updateData.timestamp = createdAt.toDate().getTime();
            } else if (createdAt.seconds) {
              updateData.timestamp = createdAt.seconds * 1000;
            }
            needsUpdate = true;
          }
          
          if (needsUpdate) {
            needsUpdateCount++;
            // Batch update yerine tek tek update (güvenlik için)
            await updateDoc(likeDoc.ref, updateData);
            updatedCount++;
            
            if (updatedCount % 10 === 0) {
              console.log(`📊 İlerleme: ${updatedCount}/${needsUpdateCount} like güncellendi`);
            }
          }
        } catch (error) {
          errorCount++;
          console.error(`Like ${likeDoc.id} güncellenirken hata:`, error);
        }
      }
      
      console.log(`✅ Likes format düzeltme tamamlandı:`);
      console.log(`  - Güncellenmesi gereken: ${needsUpdateCount}`);
      console.log(`  - Başarıyla güncellenen: ${updatedCount}`);
      console.log(`  - Hata alan: ${errorCount}`);
      
      return {
        success: true,
        total: likesSnapshot.size,
        needsUpdateCount,
        updatedCount,
        errorCount,
        message: `${updatedCount} like kaydı Flutter uyumlu hale getirildi!`
      };
    } catch (error) {
      console.error('Likes format düzeltme hatası:', error);
      return {
        success: false,
        message: `Hata: ${error}`
      };
    }
  }
}; 